import axios from "axios";
import { hardwareBridge } from '../../../services/hardwareBridge';
import { Storage } from "@/services/Storage";
import store from "@/store";
import router from "@/router";
import { generateUUID } from '@/utils/uuid';
import outbox from '@/services/outbox';
import voidOutbox from '@/services/voidOutbox';
import connectivityMonitor from '@/services/connectivityMonitor';
import vatCalc from '@/services/vatCalc';
import Swal from 'sweetalert2';
import OrdersService from '@/services/OrdersService';
import LedConfig from '@/services/LedConfig';
// Centralised LED off-switch gate. When the user has disabled the LED
// display in Settings, we must not touch the COM port from any code path
// (cart-change broadcast, payment complete, invoice next-sale) so the
// serial port stays free for other programs.
import { isLedDisabled } from '@/services/ledGate';
// docs/FRONTEND.md § Sales-write user field — #26328 / Sentry 1V+1E
import { requireSalesWriteUser } from '@/services/salesWriteUser';
// Filter voided/archived lines out of invoice payloads (defense vs BE "no invoice lines specied").
// docs: src/utils/activeSaleLines.js; docs/FRONTEND.md invoice + void
import { filterActiveSaleLines } from '@/utils/activeSaleLines';

const getStoreDB = async () => {
    const cached = store.getters.getDefaultStore;
    if (cached && cached.StoreDB) return cached.StoreDB;
    const s = await Storage.getDefaultStore();
    return s?.StoreDB;
};

const getLineVatBase = (line) => {
    const qty = Number(line.Qty) || 0;
    const unit = Number(line.Unit) || 0;
    let base = (unit * qty) - Number(line.Discount || 0);
    if (line.instructions && line.instructions.length > 0) {
        const instructionTotal = line.instructions
            .map(i => Number(i.Price) * Number(i.count || 1))
            .reduce((acc, n) => Number(acc) + Number(n), 0);
        base += Number(instructionTotal);
    }
    return Number(base) || 0;
};

/**
 * Net total for a sale line. Used for the `Line` field sent to the
 * backend and for any in-memory recomputation that mirrors it.
 *
 * Formula: ((Unit + tourismLevy) * Qty) + instructionTotal - Discount
 *
 * This is the same shape used by:
 *   - CurrentSaleLine.vue display formula (line 68)
 *   - CurrentSaleTable.vue getSaleLineDisplayTotal (line 364)
 *   - updateSaleLineDiscount mutation (line 697)
 *   - getSaleTotal getter (line 514)
 *   - calcLineBase helper (line 1293)
 *
 * Centralising the formula here means future changes (e.g. adding
 * insurance levy) only need to happen in one place. Before this helper
 * existed, six mutation sites in this file and the parseInvoice action
 * each computed `Line = Unit * Qty - Discount` and silently dropped
 * instruction costs — that is the bug Felix reported on 2026-06-23.
 *
 * @param {Object} line sale line item ({Qty, Unit, tourismLevy,
 *   Discount, instructions?})
 * @returns {number} net line total in the sale currency, rounded to 2dp;
 *   never negative (a value discount larger than the line caps at 0).
 */
const getLineNetTotal = (line) => {
    if (!line) return 0;
    const qty = Number(line.Qty) || 0;
    const unit = Number(line.Unit) || 0;
    const levy = Number(line.tourismLevy || 0) || 0;
    const instr = Array.isArray(line.instructions) && line.instructions.length > 0
        ? line.instructions
            .map(i => Number(i.Price || 0) * Number(i.count || i.Qty || 1))
            .reduce((acc, n) => Number(acc) + Number(n), 0)
        : 0;
    const discount = Number(line.Discount || 0) || 0;
    const raw = ((unit + levy) * qty) + instr - discount;
    const total = raw < 0 ? 0 : raw;
    return Number(total.toFixed(2));
};

const getLineTaxMeta = (line) => {
    const stockItem = store.getters.getByPlu ? store.getters.getByPlu(line.Plu) : null;
    let taxLabel = line.TaxLabel || line.taxLabel || '';
    if (!taxLabel && stockItem && stockItem.TaxLabels) {
        const taxCategory = store.getters.getZraCategoryByLabel
            ? store.getters.getZraCategoryByLabel(stockItem.TaxLabels)
            : null;
        taxLabel = taxCategory ? taxCategory.TaxLabel : '';
    }
    if (!taxLabel && stockItem) {
        taxLabel = stockItem.TaxLabel || stockItem.taxLabel || stockItem.Tax || '';
    }
    const recommendedRetail =
        Number(line.RecommendedRetail ?? line.recommendedRetail ?? line.mtv) ||
        Number(stockItem?.RecommendedRetail ?? stockItem?.recommendedRetail ?? stockItem?.StockSetting?.mtv) ||
        0;
    return { taxLabel, recommendedRetail };
};

const updateLineVat = async (line, storeDB) => {
    if (!line) return;
    const db = storeDB || await getStoreDB();
    const { taxLabel, recommendedRetail } = getLineTaxMeta(line);
    line.TaxLabel = taxLabel || line.TaxLabel || '';
    line.RecommendedRetail = recommendedRetail;
    const base = getLineVatBase(line);
    const vat = await vatCalc.getVatTotal(base, true, (taxLabel || ''), recommendedRetail, db);
    line.vat = Number(vat || 0);
};

const appendDocumentPrintType = (url, settings) => {
    if (settings?.A4Type !== 'A5') return url;
    return `${url}${url.includes('?') ? '&' : '?'}A4Type=A5`;
};

const summarizeBroadcastLines = (lines = []) => lines.map(line => ({
    index: line.index,
    plu: line.Plu || '',
    description: line.Description || '',
    qty: Number(line.Qty) || 0,
    unit: Number(line.Unit) || 0,
    line: Number(line.Line) || 0,
    discount: Number(line.Discount) || 0,
}));

// Broadcast sale updates to customer display
const broadcastSaleUpdate = (state) => {
    if (!window.screenAPI || !window.screenAPI.sendSaleData) {
        console.log('[CustomerDisplay] Broadcast skipped: screenAPI.sendSaleData unavailable');
        return;
    }

    try {
        const lines = state.currentSaleLines || [];

        // Calculate total similar to getSaleTotal
        const total = lines.map(line => {
            let t = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0)) - Number(line.Discount || 0);
            if (line.instructions && line.instructions.length > 0) {
                let instTotal = line.instructions.reduce((acc, i) => Number(acc) + (Number(i.Price) * Number(i.count)), 0);
                t += Number(instTotal);
            }
            return Number(t);
        }).reduce((sum, val) => Number(sum) + Number(val), 0);

        const vat = lines.map(l => Number(l.vat || 0)).reduce((sum, v) => Number(sum) + Number(v), 0);
        const subtotal = total - vat;

        // Get currency from store
        const currency = store.getters.getBaseCurrency || { Symbol: 'K', Name: 'Kwacha' };

        const payload = {
            items: lines.map(l => ({
                plu: l.Plu || '',
                name: l.Description || 'Unknown Item',
                quantity: Number(l.Qty) || 0,
                unitPrice: Number(l.Unit) || 0,
                lineTotal: Number(l.Line) || 0,
                discount: Number(l.Discount) || 0,
                taxLabel: l.TaxLabel || ''
            })),
            totals: {
                subtotal: Number(subtotal) || 0,
                tax: Number(vat) || 0,
                grandTotal: Number(total) || 0
            },
            currency: currency,
            storeName: store.getters.getDefaultStore ? store.getters.getDefaultStore.StoreName : 'Lytning POS'
        };

        console.log('[CustomerDisplay] Broadcasting sale update', {
            itemCount: lines.length,
            totals: payload.totals,
            currency: payload.currency?.Symbol || payload.currency?.Name || '',
            lines: summarizeBroadcastLines(lines),
        });
        window.screenAPI.sendSaleData(payload).catch(e => console.error('[CustomerDisplay] Update failed:', e));
    } catch (e) {
        console.error('[CustomerDisplay] Error preparing update:', e);
    }
};

// Keep cooking instruction counts in sync with qty changes (scale proportionally)
const adjustInstructionCountsForQtyChange = (line, oldQty, newQty) => {
    if (!line || !Array.isArray(line.instructions) || line.instructions.length === 0) return;
    const prevQty = Number(oldQty) || 0;
    const nextQty = Number(newQty) || 0;
    if (prevQty === nextQty) return;

    line.instructions = line.instructions.map(inst => {
        const current = Number(inst.count || 0);
        if (prevQty <= 0) {
            return { ...inst, count: Number((current * nextQty).toFixed(3)) };
        }
        const perUnit = current / prevQty;
        const next = Number((perUnit * nextQty).toFixed(3));
        return { ...inst, count: next };
    }).filter(inst => Number(inst.count || 0) > 0);
};

const getLedSettings = () => {
    const settings = store.getters.getDeviceSettings || store.state.device_settings || store.state.settings || {};
    return LedConfig.getEffective(settings);
};

// Broadcast sale updates to LED pole display
// Note: This is synchronous because it's called from mutations
// LED settings are read from the store's settings object directly
const broadcastLedUpdate = (state) => {
    if (!window.ledDisplay || !window.ledDisplay.showWithMode) {
        console.log('[LED] Broadcast skipped: ledDisplay.showWithMode unavailable');
        return;
    }

    try {
        // Get LED settings from best available source (synchronous)
        const ledSettings = getLedSettings();

        // Off-switch: when the user has disabled the LED display we must
        // not open the COM port on every cart mutation, otherwise other
        // programs cannot use the same serial port.
        if (isLedDisabled(ledSettings)) {
            console.log('[LED] Broadcast skipped: LED display disabled in settings');
            return;
        }

        const port = ledSettings.ledPort || 'COM2';
        const baudRate = ledSettings.ledBaudRate || 2400;

        const lines = state.currentSaleLines || [];
        console.log('[LED] Preparing sale broadcast', {
            itemCount: lines.length,
            port,
            baudRate,
            enabled: ledSettings.enableLedDisplay,
            lines: summarizeBroadcastLines(lines),
        });

        // Calculate total
        const total = lines.reduce((sum, line) => {
            let lineTotal = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0)) - Number(line.Discount || 0);
            if (line.instructions && line.instructions.length > 0) {
                lineTotal += line.instructions.reduce((acc, i) => Number(acc) + (Number(i.Price) * Number(i.count)), 0);
            }
            return Number(sum) + Number(lineTotal);
        }, 0);

        console.log('[LED] Calculated sale total for broadcast', {
            total: total.toFixed(2),
            itemCount: lines.length,
        });

        // If no items, clear the display
        if (lines.length === 0) {
            console.log('[LED] Broadcasting clear because sale is empty', { port, baudRate });
            window.ledDisplay.clear(port, baudRate).catch(e => console.error('[LED] Clear failed:', e));
            return;
        }

        // Display total on LED
        console.log('[LED] Broadcasting sale total to LED', {
            mode: 'total',
            value: total.toFixed(2),
            port,
            baudRate,
        });
        window.ledDisplay.showWithMode('total', total.toFixed(2), port, baudRate)
            .catch(e => console.error('[LED] Display total failed:', e));
    } catch (e) {
        console.error('[LED] Error updating display:', e);
    }
};

const clearOpenTableBannerState = () => {
    if (store.state?.tableModule) {
        store.state.tableModule.openTableName = null;
    }
};

let getNow = function () {
    let date = new Date();
    let d = date.toISOString().split('T')[0]
    let t = date.toISOString().split('T')[1].split('.')[0];
    return d + ` ` + t;
}

let getLocalNow = function () {
    const date = new Date();
    const pad = (num) => num.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
const isEmpty = function (obj) {
    if (!obj) {
        return true;
    }
    return Object.keys(obj).length === 0;
}
let validateSecurity = function (security) {
    return store.dispatch(`validateSecurityPermission`, security);
}

// Check if a special is within its valid time range
const isSpecialActive = (special) => {
    if (!special.StartDate || !special.EndDate) return true; // No time limits, always active

    const now = new Date();
    const startDate = new Date(special.StartDate);
    const endDate = new Date(special.EndDate);

    return now >= startDate && now <= endDate;
};

// Debug helper for combo pricing issues. Enable in console with:
// window.__comboDebug = true
const isComboDebugEnabled = () => {
    try {
        return Boolean(window && window.__comboDebug);
    } catch (e) {
        return false;
    }
};

const comboDebugLog = (...args) => {
    if (!isComboDebugEnabled()) return;
    console.log('[ComboDebug]', ...args);
};

let saleRecalcQueued = false;
const scheduleSaleRecalc = () => {
    if (saleRecalcQueued) return;
    saleRecalcQueued = true;
    const enqueue = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (cb) => setTimeout(cb, 0);
    enqueue(() => {
        saleRecalcQueued = false;
        store.commit('recalcSpecials');
    });
};

/**
 * Build a void receipt as ESC/POS commands and return a base64 string.
 * Extracted from printVoidOffline so it can be reused for item-printer void slips.
 * @param {Object} data - { user, pc, reason, supervisor, items: [{description, qty, value}] }
 * @param {Array}  items - items to include (may be a subset of data.items)
 * @param {Object} store - store info from Storage.getDefaultStore()
 * @param {Object} settings - device settings from Storage.getDeviceSettings()
 * @returns {string} base64 encoded ESC/POS byte array
 */
function buildVoidReceiptEscPos(data, items, store, settings) {
    const ESC = 0x1B;
    const GS = 0x1D;

    const receiptType = settings?.ReceiptType || 'Normal';
    const is55mm = receiptType === '55ml';
    const lineWidth = is55mm ? 32 : 48;

    const commands = [];

    // Initialize printer
    commands.push(ESC, 0x40);

    // Store name - Bold, Center, Large
    commands.push(ESC, 0x61, 0x01); // Center align
    commands.push(ESC, 0x45, 0x01); // Bold on
    commands.push(GS, 0x21, 0x11); // Double width & height
    const storeName = store?.StoreName || 'Store';
    commands.push(...Array.from(storeName).map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(GS, 0x21, 0x00); // Reset size
    commands.push(ESC, 0x45, 0x00); // Bold off

    // Location
    const location = store?.Location || '';
    if (location) {
        commands.push(...Array.from(location).map(c => c.charCodeAt(0)));
        commands.push(0x0A);
    }

    // Store details
    const storeDetails = store?.StoreDetails || '';
    if (storeDetails) {
        commands.push(...Array.from(storeDetails).map(c => c.charCodeAt(0)));
        commands.push(0x0A);
    }

    // TPIN
    let tpin = store?.TPIN || '';
    if (!tpin) {
        const addr3 = store?.Addr3 || '';
        const match = String(addr3).match(/\b\d{6,}\b/);
        if (match) tpin = match[0];
    }
    if (tpin) {
        commands.push(...Array.from(`TPIN : ${tpin}`).map(c => c.charCodeAt(0)));
        commands.push(0x0A);
    }

    commands.push(0x0A); // Empty line

    // "Void Receipt" header - Bold, Center
    commands.push(ESC, 0x45, 0x01); // Bold on
    commands.push(...Array.from('Void Receipt').map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(ESC, 0x45, 0x00); // Bold off

    // Separator
    commands.push(ESC, 0x61, 0x00); // Left align
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // User
    commands.push(...Array.from(`User          : ${data.user || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // PC
    commands.push(...Array.from(`PC            : ${data.pc || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Separator
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Items
    if (items && Array.isArray(items)) {
        items.forEach(item => {
            commands.push(...Array.from(`  Description  : ${item.description || 'N/A'}`).map(c => c.charCodeAt(0)));
            commands.push(0x0A);
            const qty = item.qty || 0;
            const value = item.value || 0;
            commands.push(...Array.from(`  Quantity: ${qty}          Value: ${value}`).map(c => c.charCodeAt(0)));
            commands.push(0x0A);
        });
    }

    // Separator
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Reason
    commands.push(...Array.from(`Reason        : ${data.reason || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Supervisor
    commands.push(...Array.from(`Supervisor    : ${data.supervisor || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Separator
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(0x0A); // Empty line

    // Footer
    commands.push(...Array.from('Software by www.chrilantech.com').map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(0x0A, 0x0A, 0x0A, 0x0A); // Feed lines
    commands.push(ESC, 0x64, 0x08); // Feed 8 lines
    commands.push(GS, 0x56, 0x00); // Full cut

    // Convert to base64
    const buffer = new Uint8Array(commands);
    return btoa(String.fromCharCode.apply(null, buffer));
}

export default {
    name: 'sales',
    state: {
        saleDiscount: {},
        currentSaleLines: [],
        latestInvoice: {},
        refundInv: null,
        discountSpecials: [],
        comboSpecials: [],
        multiSpecials: [],
        tip: 0,
        lpo: null,
        saleDetails: {},
        // Controls whether sale-level discount is redistributed across items
        distributeSaleLevelDiscount: false,
        isSaleProcessing: false,

        lastSaleChange: 0,
        lastSaleInvoice: null,
        showChangeNotification: false,
        // Optimized lookups
        discountSpecialsMap: {},
        multiSpecialsMap: {},
        comboSpecialsMap: {},
    },
    getters: {
        getLatestInvoice: (state) => state.latestInvoice,
        getRefundInvoice: (state) => state.refundInv,
        getCurrentSale: (state) => state.currentSale,
        getDiscountSpecials: (state) => state.discountSpecials,
        getComboSpecials: (state) => state.comboSpecials,
        getMultiSpecials: (state) => state.multiSpecials,
        getSaleLines: (state) => state.currentSaleLines,
        getItemSpecialPrice: (state) => (itemPLU) => {
            // Pure function: compute potential special price for display only. No state mutations.
            let specialPrice = null;

            // Discount Special (flat special price)
            let disc = state.discountSpecialsMap[itemPLU];
            if (disc && isSpecialActive(disc)) {
                specialPrice = Number(disc.SpePrice) || null;
            }

            // Combo Special: if item is part of combo and counterpart exists in current sale, show combo price
            if (!specialPrice) {
                const comboEntry = state.comboSpecialsMap[itemPLU];
                if (comboEntry && isSpecialActive(comboEntry)) {
                    const specialID = comboEntry.special_ID;
                    const groupID = Number(comboEntry.group);
                    const otherGroup = groupID === 1 ? 2 : 1;
                    // For combo validation, we still need to scan current lines or have a map of current lines?
                    // currentSaleLines is usually small (<100), so scanning it is fine.
                    // But we need to find "other items" in the combo definition.
                    // We can use the array state.comboSpecials for "other items" search OR keep a secondary map.
                    // Given we have the specialID, we could optimize this, but let's stick to the existing logic
                    // just optimized to find the initial comboEntry.

                    const otherItems = state.comboSpecials.filter(sp => sp.special_ID === specialID && Number(sp.group) === otherGroup);
                    const hasCounterpart = state.currentSaleLines.some(line => otherItems.find(oi => oi.plu === line.Plu));
                    if (hasCounterpart) {
                        try {
                            const counterpartPlu = state.currentSaleLines
                                .map(l => l.Plu)
                                .filter(plu => otherItems.some(oi => oi.plu === plu));
                            // console.log('[ComboSpecial] Applying combo price', {
                            //     itemPLU,
                            //     specialID,
                            //     groupID,
                            //     price: comboEntry.Price,
                            //     counterpartPlu
                            // });
                        } catch (e) {
                            console.warn('[ComboSpecial] Log failed', e);
                        }
                        specialPrice = Number(comboEntry.Price) || null;
                    }
                }
            }

            // Multi-Quantity Special: if any line for this PLU meets threshold, show special unit price
            if (!specialPrice) {
                const multi = state.multiSpecialsMap[itemPLU];
                if (multi && isSpecialActive(multi)) {
                    const threshold = Number(multi.HowMany) || 0;
                    const unit = Number(multi.Price);
                    const hasEnough = state.currentSaleLines.some(line => line.Plu === itemPLU && Number(line.Qty) >= threshold);
                    if (hasEnough && threshold > 0) specialPrice = unit;
                }
            }

            return specialPrice;
        },
        getTipAmount: (state) => state.tip,
        getLPO: (state) => state.lpo,
        getSaleTotal: (state) => {
            let saleLines = state.currentSaleLines;
            if (!saleLines || saleLines.length === 0) return 0.00;

            return saleLines.map(line => {
                let total = (line.Qty * line.Unit) + (line.Qty * line.tourismLevy) - line.Discount;

                // Check for instructions for each line item
                if (line.instructions && line.instructions.length > 0) {
                    let instructionTotal = line.instructions.map(i => i.Price * i.count).reduce((acc, n) => Number(n) + Number(acc), 0);
                    total += Number(instructionTotal); // Multiply by Qty to account for multiple instances
                }

                return Number(total);
            }).reduce((sum, line) => Number(sum) + Number(line), 0);
        },
        getSaleDiscount: state => {
            if (!isEmpty(state.saleDiscount)) {
                return state.saleDiscount
            } else return {
                type: 'none', value: 0, total: 0
            }
        },
        getSaleUnits: state => state.currentSaleLines.length,
        getSaleVat: (state, getters) => {
            return (state.currentSaleLines || [])
                .map(l => Number(l.vat || 0))
                .reduce((sum, v) => Number(sum) + Number(v), 0);
        },
        getSubTotal: (state, getters) => {
            return getters.getSaleTotal - getters.getSaleVat
        },
        getSaleDetails: (state) => state.saleDetails,
        getLastSaleChange: (state) => state.lastSaleChange,
        getLastSaleInvoice: (state) => state.lastSaleInvoice,
        getShowChangeNotification: (state) => state.showChangeNotification,
        getSaleProcessingState: (state) => state.isSaleProcessing === true
    },
    mutations: {
        updateSaleLinePrice: async (state, data) => {
            console.log('[updateSaleLinePrice] Called with data:', data);
            const storeDB = await getStoreDB();
            // For Custom price type, we don't need to fetch stock - just use the provided values
            if (data.priceType === "Custom") {
                console.log('[updateSaleLinePrice] Processing Custom price for index:', data.index);
                await Promise.all(state.currentSaleLines.map(async (line) => {
                    if (line.index === data.index) {
                        const oldQty = Number(line.Qty);
                        // Use provided values directly
                        line.Unit = Number(data.Unit);
                        line.baseUnit = Number(data.Unit);

                        if (data.Qty !== undefined) {
                            line.Qty = Number(data.Qty);
                            adjustInstructionCountsForQtyChange(line, oldQty, line.Qty);
                        }

                        if (data.Line !== undefined) {
                            line.Line = Number(data.Line);
                        } else {
                            // Use the canonical helper so cooking-instruction costs
                            // are not silently dropped from `Line`. See getLineNetTotal.
                            line.Line = getLineNetTotal(line);
                        }
                        await updateLineVat(line, storeDB);
                    }
                }));
            } else {
                // For predefined price levels, fetch stock and update
                // Use Promise.all to await all updates if multiple lines could match,
                // though typically only one line.index matches data.index.
                // The async in forEach is an anti-pattern, better to map and await all.
                await Promise.all(state.currentSaleLines.map(async (line) => {
                    if (line.index === data.index) {
                        const oldQty = Number(line.Qty);
                        let currentPrice = line.Unit;
                        let stock1 = await Storage.getAllStock();
                        let item = stock1.find(stock => stock.PLU === data.Plu);

                        if (item) {
                            switch (data.priceType) {
                                case "Normal":
                                    currentPrice = item.SellingIncl;
                                    break;
                                case "At Cost":
                                    currentPrice = item.InclCost;
                                    break;
                                case "Price 1":
                                    currentPrice = item.price1;
                                    break;
                                case "Price 2":
                                    currentPrice = item.price2;
                                    break;
                                case "Price 3":
                                    currentPrice = item.price3;
                                    break;
                                case "Price 4":
                                    currentPrice = item.price4;
                                    break;
                                case "Price 5":
                                    currentPrice = item.price5;
                                    break;
                                default:
                                    currentPrice = data.Unit !== undefined ? data.Unit : line.Unit;
                                    break;
                            }
                        }

                        // Update the line
                        line.Unit = currentPrice;
                        line.baseUnit = Number(currentPrice);

                        if (data.Qty !== undefined) {
                            line.Qty = Number(data.Qty);
                            adjustInstructionCountsForQtyChange(line, oldQty, line.Qty);
                        }

                        // Recalculate from the canonical line-total formula so
                        // cooking-instruction costs are preserved. See getLineNetTotal.
                        const newLine = getLineNetTotal(line);
                        line.Line = newLine;
                        await updateLineVat(line, storeDB);
                    }
                }));
            }

            // After updating price level, re-evaluate specials
            store.commit('recalcSpecials');
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        reduceQuantity: async (state, saleLine) => {
            comboDebugLog('reduceQuantity:before', {
                targetIndex: saleLine?.index,
                lines: (state.currentSaleLines || []).map(l => ({
                    index: l.index,
                    plu: l.Plu,
                    qty: Number(l.Qty) || 0,
                    unit: Number(l.Unit) || 0,
                    baseUnit: Number(l.baseUnit) || 0,
                    discount: Number(l.Discount) || 0,
                    specialType: l.specialType || null,
                })),
            });
            const line = state.currentSaleLines.find(line => line.index === saleLine.index);
            if (line && line.Qty > 0) {
                const storeDB = await getStoreDB();
                const oldQty = Number(line.Qty);
                line.Qty -= 1;
                adjustInstructionCountsForQtyChange(line, oldQty, line.Qty);
                // Use the canonical helper so instruction costs are preserved on
                // every qty change. See getLineNetTotal.
                line.Line = getLineNetTotal(line);
                await updateLineVat(line, storeDB);
                if (line.Qty <= 0) {
                    state.currentSaleLines = state.currentSaleLines.filter(l => l.index !== line.index);
                    store.commit('reIndexSale');
                }
                // Re-evaluate specials after quantity reduction
                store.commit('recalcSpecials');
                comboDebugLog('reduceQuantity:after-recalc', {
                    targetIndex: saleLine?.index,
                    lines: (state.currentSaleLines || []).map(l => ({
                        index: l.index,
                        plu: l.Plu,
                        qty: Number(l.Qty) || 0,
                        unit: Number(l.Unit) || 0,
                        baseUnit: Number(l.baseUnit) || 0,
                        discount: Number(l.Discount) || 0,
                        specialType: l.specialType || null,
                    })),
                });
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
            }
        },
        updateSaleLineQty: async (state, payload) => {
            const { saleLine, qty } = payload;
            const line = state.currentSaleLines.find(line => line.index === saleLine.index);
            if (line) {
                const storeDB = await getStoreDB();
                const oldQty = Number(line.Qty);
                line.Qty = qty;
                adjustInstructionCountsForQtyChange(line, oldQty, line.Qty);
                // Use the canonical helper so instruction costs are preserved on
                // explicit qty sets. See getLineNetTotal.
                line.Line = getLineNetTotal(line);
                await updateLineVat(line, storeDB);
                // Re-evaluate specials after any explicit qty set
                store.commit('recalcSpecials');
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
            }
        },
        updateSaleLineDiscount: async (state, payload) => {
            // Supports payloads:
            // - { saleLine, discount }  // backward compatibility: fixed amount
            // - { saleLine, value, mode } where mode in ['value','percent']
            const { saleLine } = payload;
            const line = state.currentSaleLines.find(l => l.index === saleLine.index);
            if (!line) return;

            // Compute base = (Unit + tourismLevy) * Qty + sum(instruction.Price * instruction.count)
            const qty = Number(line.Qty) || 0;
            const unit = Number(line.Unit) || 0;
            const levy = Number(line.tourismLevy || 0) || 0;
            let base = (unit + levy) * qty;
            if (line.instructions && line.instructions.length > 0) {
                const instructionTotal = line.instructions
                    .map(i => Number(i.Price) * Number(i.count || 1))
                    .reduce((acc, n) => Number(acc) + Number(n), 0);
                base += Number(instructionTotal);
            }
            base = Number(base) || 0;

            // Determine discount value
            let d = 0;
            if (payload.mode === 'percent' || payload.discountType === 'percent') {
                const pct = Math.max(0, Math.min(100, Number(payload.value ?? payload.discountAmount ?? 0)));
                d = Number(((pct / 100) * base).toFixed(2));
            } else if (payload.mode === 'value' || payload.discountType === 'value') {
                d = Number(payload.value ?? payload.discount ?? payload.discountAmount ?? 0);
            } else {
                // Backward compatibility: payload.discount as value
                d = Number(payload.discount ?? 0);
            }

            if (isNaN(d) || d < 0) d = 0;
            if (d > base) d = base;

            line.Discount = d;
            line.Line = Number((base - d).toFixed(2));
            const storeDB = await getStoreDB();
            await updateLineVat(line, storeDB);
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        // Recalculate and apply specials based on current quantities and loaded specials
        recalcSpecials: async (state) => {
            comboDebugLog('recalc:start', (state.currentSaleLines || []).map(l => ({
                index: l.index,
                plu: l.Plu,
                qty: Number(l.Qty) || 0,
                unit: Number(l.Unit) || 0,
                baseUnit: Number(l.baseUnit) || 0,
                discount: Number(l.Discount) || 0,
                specialType: l.specialType || null,
            })));
            // Helper: find discount special by PLU
            const findDiscount = (plu) => state.discountSpecialsMap[plu];
            const findMulti = (plu) => state.multiSpecialsMap[plu];

            state.currentSaleLines = state.currentSaleLines.map(line => {
                const stockItem = store.getters.getByPlu ? store.getters.getByPlu(line.Plu) : null;
                const stockUnit = Number(stockItem && stockItem.SellingIncl);
                const comboEntryForLine = state.comboSpecialsMap[line.Plu];

                // Ensure baseUnit exists for reversion
                if (line.baseUnit == null) {
                    if (!isNaN(stockUnit) && stockUnit > 0) line.baseUnit = stockUnit;
                    else line.baseUnit = Number(line.Unit);
                }

                // Repair legacy lines where baseUnit was captured as combo price.
                // If no counterpart exists anymore, restore baseUnit from stock.
                if (comboEntryForLine && isSpecialActive(comboEntryForLine)) {
                    const specialID = comboEntryForLine.special_ID;
                    const groupID = Number(comboEntryForLine.group);
                    const otherGroup = groupID === 1 ? 2 : 1;
                    const otherItems = state.comboSpecials.filter(sp =>
                        sp.special_ID === specialID && Number(sp.group) === otherGroup
                    );
                    const hasCounterpartNow = state.currentSaleLines.some(l =>
                        otherItems.find(oi => oi.plu === l.Plu)
                    );
                    const comboUnit = Number(comboEntryForLine.Price);
                    const currentBase = Number(line.baseUnit);
                    const baseLooksLikeCombo = !isNaN(currentBase) && !isNaN(comboUnit) && Math.abs(currentBase - comboUnit) < 0.0001;
                    if (!hasCounterpartNow && baseLooksLikeCombo && !isNaN(stockUnit) && stockUnit > 0) {
                        line.baseUnit = stockUnit;
                        comboDebugLog('recalc:repair-baseUnit', {
                            plu: line.Plu,
                            oldBaseUnit: currentBase,
                            repairedBaseUnit: stockUnit,
                            comboUnit,
                            specialID,
                            groupID,
                        });
                    }
                }

                // Reset to base
                line.Unit = Number(line.baseUnit);
                line.specialActive = false;
                line.specialType = null;
                line.specialNote = null;

                const qty = Number(line.Qty) || 0;

                // Manual-priced lines are pinned to their entered unit price.
                // Use the canonical helper so instruction costs are preserved.
                // See getLineNetTotal.
                if (line.manualPrice) {
                    line.Line = getLineNetTotal(line);
                    return line;
                }

                // Apply Multi-Quantity Special first (min-set quantity logic)
                const multi = findMulti(line.Plu);
                if (multi && isSpecialActive(multi)) {
                    const threshold = Number(multi.HowMany) || 0;
                    const specialUnit = Number(multi.Price);
                    if (qty >= threshold && threshold > 0) {
                        line.Unit = specialUnit;
                        line.specialActive = true;
                        line.specialType = 'multi';
                        line.specialNote = `Special: ${threshold}+ @ ${specialUnit}`;
                    }
                }

                // If no multi applied, apply Discount Special (flat special price)
                if (!line.specialActive) {
                    const disc = findDiscount(line.Plu);
                    if (disc && disc.SpePrice != null && isSpecialActive(disc)) {
                        const specialUnit = Number(disc.SpePrice);
                        if (!isNaN(specialUnit) && specialUnit > 0) {
                            line.Unit = specialUnit;
                            line.specialActive = true;
                            line.specialType = 'discount';
                            line.specialNote = `Special Price @ ${specialUnit}`;
                        }
                    }
                }

                // If no other special applied, apply Combo Special
                if (!line.specialActive) {
                    const comboEntry = state.comboSpecialsMap[line.Plu];
                    if (comboEntry && isSpecialActive(comboEntry)) {
                        const specialID = comboEntry.special_ID;
                        const groupID = Number(comboEntry.group);
                        const otherGroup = groupID === 1 ? 2 : 1;
                        const otherItems = state.comboSpecials.filter(sp =>
                            sp.special_ID === specialID && Number(sp.group) === otherGroup
                        );
                        const hasCounterpart = state.currentSaleLines.some(l =>
                            otherItems.find(oi => oi.plu === l.Plu)
                        );
                        comboDebugLog('recalc:combo-check', {
                            linePlu: line.Plu,
                            specialID,
                            groupID,
                            otherGroup,
                            hasCounterpart,
                            otherItems: otherItems.map(oi => oi.plu),
                            currentPlus: state.currentSaleLines.map(l => l.Plu),
                        });
                        if (hasCounterpart) {
                            const specialUnit = Number(comboEntry.Price);
                            if (!isNaN(specialUnit) && specialUnit > 0) {
                                line.Unit = specialUnit;
                                line.specialActive = true;
                                line.specialType = 'combo';
                                line.specialNote = `Combo Price @ ${specialUnit}`;
                                const counterpartPlu = state.currentSaleLines
                                    .map(l => l.Plu)
                                    .filter(plu => otherItems.some(oi => oi.plu === plu));
                                comboDebugLog('recalc:combo-applied', {
                                    plu: line.Plu,
                                    specialID,
                                    groupID,
                                    price: specialUnit,
                                    counterpartPlu
                                });
                            }
                        }
                    }
                }

                // Recompute line total via the canonical helper so cooking-instruction
                // costs and tourism levy are included. The previous formula silently
                // dropped instruction costs here. See getLineNetTotal.
                line.Line = getLineNetTotal(line);
                if (state.comboSpecialsMap[line.Plu]) {
                    comboDebugLog('recalc:line-result', {
                        index: line.index,
                        plu: line.Plu,
                        qty,
                        unit: Number(line.Unit) || 0,
                        baseUnit: Number(line.baseUnit) || 0,
                        discount: Number(line.Discount) || 0,
                        lineTotal: Number(line.Line) || 0,
                        specialType: line.specialType || null,
                    });
                }
                return line;
            });

            const storeDB = await getStoreDB();
            await Promise.all(state.currentSaleLines.map(line => updateLineVat(line, storeDB)));

            // Broadcast to customer display and LED
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
            try {
                localStorage.setItem('currentSaleLines', JSON.stringify(state.currentSaleLines));
            } catch (e) {
                console.warn('Failed to persist currentSaleLines after add:', e);
            }
        },
        generateSaleUUID: (state) => state.saleUUID = generateUUID(),
        setDiscountSpecials: async (state, payload) => {
            if (!payload) payload = await Storage.getDiscountSpecials()
            // Filter out specials that are not within their valid time range
            payload = payload.filter(special => isSpecialActive(special));
            state.discountSpecials = payload;
            // Build Map
            const map = {};
            if (payload && Array.isArray(payload)) {
                payload.forEach(sp => {
                    if (sp.Plu) map[sp.Plu] = sp;
                });
            }
            state.discountSpecialsMap = map;
            // Re-evaluate specials when list changes
            store.commit('recalcSpecials');
        },
        setSaleCurrency: async (state, payload) => {
            if (payload) state.saleCurrency = payload
            else state.saleCurrency = await Storage.getBaseCurrency();
        },

        setMultiSpecials: async (state, payload) => {
            if (!payload) payload = await Storage.getMultiSpecials()
            // Filter out specials that are not within their valid time range
            payload = payload.filter(special => isSpecialActive(special));
            state.multiSpecials = payload;
            // Build Map
            const map = {};
            if (payload && Array.isArray(payload)) {
                payload.forEach(sp => {
                    if (sp.Plu) map[sp.Plu] = sp;
                });
            }
            state.multiSpecialsMap = map;
            // Re-evaluate specials when list changes
            store.commit('recalcSpecials');
        },

        setComboSpecials: async (state, payload) => {
            if (!payload) payload = await Storage.getComboSpecials()
            // Filter out specials that are not within their valid time range
            payload = payload.filter(special => isSpecialActive(special));
            state.comboSpecials = payload;
            // Build Map
            const map = {};
            if (payload && Array.isArray(payload)) {
                payload.forEach(sp => {
                    if (sp.plu) map[sp.plu] = sp;
                });
            }
            state.comboSpecialsMap = map;
            // Re-evaluate specials when list changes
            store.commit('recalcSpecials');
        },

        setCurrentSale: (state, data) => state.currentSale = data,
        setSaleDetails: (state, data) => state.saleDetails = data,
        setRefundInvoice: (state, data) => {
            state.refundInv = data;
            if (data.customer) {
                state.customer = data.customer;
            }
        },
        reIndexSale: (state) => {
            let lines = state.currentSaleLines;
            for (let i = 0; i < lines.length; i++) {
                lines[i].index = i;
            }
            state.currentSaleLines = lines;
        },
        setCurrentLines: async (state, data) => {
            const lines = Array.isArray(data) ? data : [];

            // Optimization: skip processing for empty arrays
            if (lines.length === 0) {
                state.currentSaleLines = [];
                return;
            }

            state.currentSaleLines = lines.map(l => ({
                ...l,
                baseUnit: l.baseUnit != null ? Number(l.baseUnit) : Number(l.Unit),
                specialActive: Boolean(l.specialActive) || false,
                specialType: l.specialType || null,
                specialNote: l.specialNote || null,
                showMobileMenu: false,
            }));
            const storeDB = await getStoreDB();
            await Promise.all(state.currentSaleLines.map(line => updateLineVat(line, storeDB)));
            // Ensure indices are consistent
            store.commit('reIndexSale');
            // Re-evaluate specials for restored lines
            store.commit('recalcSpecials');
        },
        addToCurrentSaleLines: async (state, payload) => {
            let data = payload;
            // Determine if this is the very first line being added to the current sale
            const wasEmpty = state.currentSaleLines.length === 0;
            const normalizeManualFlag = (value) => {
                return value === true || value === 1 || value === '1' || value === 'true';
            };
            const isManualPrice = normalizeManualFlag(data.manualPrice);
            data.manualPrice = isManualPrice;
            data.Unit = Number(data.Unit);
            if (!Number.isFinite(data.Unit)) data.Unit = 0;
            data.Qty = Number(data.Qty);
            if (!Number.isFinite(data.Qty) || data.Qty <= 0) data.Qty = 1;
            data.Discount = Number(data.Discount || 0);
            if (!Number.isFinite(data.Discount) || data.Discount < 0) data.Discount = 0;
            data.Line = Number(data.Line);
            if (!Number.isFinite(data.Line)) {
                data.Line = Number((data.Unit * data.Qty - data.Discount).toFixed(2));
            }
            const normalizeBool = (value) => value === true || value === 1 || value === '1' || value === 'true';
            const dataPlu = String(data.Plu ?? '').trim();
            const dataDescription = String(data.Description ?? '').trim();

            // Determine base unit (normal price) for correct special reversion
            const stockItem = store.getters.getByPlu ? store.getters.getByPlu(data.Plu) : null;
            const inferredBaseUnit =
                (data.baseUnit != null && Number.isFinite(Number(data.baseUnit))) ? Number(data.baseUnit) :
                    isManualPrice ? Number(data.Unit) :
                        (stockItem && stockItem.SellingIncl != null && Number.isFinite(Number(stockItem.SellingIncl)) && Number(stockItem.SellingIncl) > 0) ? Number(stockItem.SellingIncl) :
                            Number(data.Unit);
            if (!data.StockSetting && !data.stockSetting && stockItem?.StockSetting) {
                data.StockSetting = stockItem.StockSetting;
            }

            const normalizeInstructions = (list) => {
                return (Array.isArray(list) ? list : []).map(i => ({
                    CookingNum: i.CookingNum,
                    CookingMsg: i.CookingMsg,
                    Price: Number(i.Price || 0),
                    count: Number(i.count || i.Qty || 1),
                })).sort((a, b) => {
                    const c1 = String(a.CookingNum).localeCompare(String(b.CookingNum));
                    if (c1 !== 0) return c1;
                    const c2 = String(a.CookingMsg).localeCompare(String(b.CookingMsg));
                    if (c2 !== 0) return c2;
                    return Number(a.Price) - Number(b.Price);
                });
            };

            const instructionsKey = (list) => JSON.stringify(normalizeInstructions(list));

            const dataInstructionsKey = instructionsKey(data.instructions);

            let item = state.currentSaleLines.find(line => {
                const lineUnit = Number(line.Unit);
                const dataUnit = Number(data.Unit);
                const unitsMatch = Number.isFinite(lineUnit) && Number.isFinite(dataUnit)
                    ? Math.abs(lineUnit - dataUnit) < 0.0001
                    : line.Unit === data.Unit;
                const linePlu = String(line.Plu ?? '').trim();
                const lineDescription = String(line.Description ?? '').trim();
                return linePlu === dataPlu &&
                    unitsMatch &&
                    normalizeManualFlag(line.manualPrice) === isManualPrice &&
                    lineDescription === dataDescription &&
                    instructionsKey(line.instructions) === dataInstructionsKey;
            });
            const cachedSettings = getLedSettings();
            const separateLineEnabled = normalizeBool(cachedSettings?.SeperateLine);
            if (item && !separateLineEnabled && !item.voucher) {
                // preserve base unit if not set
                if (item.baseUnit == null) item.baseUnit = inferredBaseUnit;
                if (!item.StockSetting && !item.stockSetting && data.StockSetting) {
                    item.StockSetting = data.StockSetting;
                }
                item.Qty = Number(item.Qty) + Number(data.Qty);
                item.Unit = Number(data.Unit);
                item.manualPrice = normalizeManualFlag(item.manualPrice) || isManualPrice;
                item.Line = Number((Number(item.Unit) * Number(item.Qty) - Number(item.Discount || 0)).toFixed(2));
                const mergedInstructions = [...(data.instructions || []), ...(item.instructions || [])];
                let edited = mergedInstructions.reduce((accumulator, msg) => {
                    if (accumulator.has(msg.CookingNum)) {
                        let m = accumulator.get(msg.CookingNum)
                        m.count += Number(msg.count || msg.Qty || 1)
                    } else {
                        accumulator.set(msg.CookingNum, {
                            ...msg,
                            Price: Number(msg.Price || 0),
                            count: Number(msg.count || msg.Qty || 1),
                        });
                    }
                    return accumulator;
                }, new Map())
                item.instructions = [...edited.values()]
            } else {
                data.index = state.currentSaleLines.length + 1
                data.savedQty = 0;
                // seed baseUnit for future recalculations
                if (data.baseUnit == null) data.baseUnit = inferredBaseUnit;
                data.Unit = Number(data.Unit);
                data.Qty = Number(data.Qty);
                data.Line = Number((Number(data.Unit) * Number(data.Qty) - Number(data.Discount || 0)).toFixed(2));
                data.instructions = normalizeInstructions(data.instructions);
                // initialize special flags
                data.specialActive = false;
                data.specialType = null;
                data.specialNote = null;
                data.showMobileMenu = false;
                // Stable client line id so backend CurrentSale.SeperateLine uses
                // UPSERT-by-id (Path 1) instead of PLU-only fallback (Path 3).
                // Without this, same-PLU + different Unit collapses into one row.
                // docs: FRONTEND.md cart line identity; BE SeperateLine Path 1
                // Same fix as Restaurant POS cc7dfec.
                if (!data.id && !data.Id && !data.lineId && !data.currentSaleLineId) {
                    data.id = generateUUID();
                } else if (!data.id) {
                    data.id = data.Id || data.lineId || data.currentSaleLineId;
                }
                state.currentSaleLines.push(data);

                // If this was the first item added to an empty sale, clear any last sale change notification/state
                if (wasEmpty) {
                    store.commit('clearLastSaleChange');
                }
            }
            const hasAnySpecials =
                (Array.isArray(state.discountSpecials) && state.discountSpecials.length > 0) ||
                (Array.isArray(state.multiSpecials) && state.multiSpecials.length > 0) ||
                (Array.isArray(state.comboSpecials) && state.comboSpecials.length > 0);

            // Fast path: if no specials are loaded, avoid full-sale recalculation.
            // We only need to refresh VAT/display/persistence for the changed line.
            if (!hasAnySpecials) {
                const changedLine = item || data;
                if (changedLine) {
                    changedLine.specialActive = false;
                    changedLine.specialType = null;
                    changedLine.specialNote = null;
                    changedLine.Line = Number((Number(changedLine.Unit) * Number(changedLine.Qty) - Number(changedLine.Discount || 0)).toFixed(2));
                    const storeDB = await getStoreDB();
                    await updateLineVat(changedLine, storeDB);
                }
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                try {
                    localStorage.setItem('currentSaleLines', JSON.stringify(state.currentSaleLines));
                } catch (e) {
                    console.warn('Failed to persist currentSaleLines after add:', e);
                }
                return;
            }

            // Defer expensive recalculation and display updates so line rendering is immediate on click.
            scheduleSaleRecalc();
        },
        /**
         * Void one unit of a cart line (PIN-gated).
         *
         * Why call logItemVoid then gate local remove by salenum (#26827 + 22844):
         * - Always POST/queue the void first so BE gets an audit trail.
         * - salenum==0 (unsaved cart): local cart is source of truth — remove the
         *   unit even when BE returns businessReject (no saleline row yet; BE
         *   audit-only). Silent UX: no toast at all — void already succeeded
         *   from the cashier's view (TicketApp #26827 Founder follow-up).
         * - salenum!=0 (saved sale): keep 22844 fail-closed — do not strip FE
         *   when BE fails to archive, or re-add+save pure-merge duplicates rows.
         *   Toast stays three-way (#26827): offline / businessReject / network.
         * docs: FRONTEND.md cart line identity; BE CartLineArchiveHelper pure-merge
         */
        voidItem: async (state, payload) => {
            // Idempotent: second void of the same index is a no-op once gone.
            let item = state.currentSaleLines.find(s => s.index === payload.index)
            if (!item) return;
            let validation = await validateSecurity(`Void`);
            if (!validation) return;

            comboDebugLog('voidItem:before', {
                voidIndex: payload?.index,
                lines: (state.currentSaleLines || []).map(l => ({
                    index: l.index,
                    plu: l.Plu,
                    qty: Number(l.Qty) || 0,
                    unit: Number(l.Unit) || 0,
                    baseUnit: Number(l.baseUnit) || 0,
                    discount: Number(l.Discount) || 0,
                    specialType: l.specialType || null,
                })),
            });

            let supervisor = await Storage.getSupervisor();
            const saleNum = Number(store.getters.getCurrentSale || state.currentSale || 0) || 0;
            // Unsaved cart lines have no DB saleline; local state owns removal.
            const isUnsavedCartLine = saleNum === 0;

            // B5b / ticket 22844: BE logItemVoid archives by currentsale.id (UUID).
            // salenum + saleline help legacy PLU fallback and partial-void qty paths.
            let voidObj = {
                VoidPlu: item.Plu,
                VoidDescritpiom: item.Description,
                VoidQty: 1,
                VoidValue: item.Unit,
                SuperVisor: supervisor,
                id: item.id || item.Id || item.lineId || item.currentSaleLineId || null,
                saleline: item.saleline != null ? item.saleline : (item.SaleLine != null ? item.SaleLine : null),
                salenum: saleNum,
                SaleNum: saleNum,
            }
            await Storage.setSupervisor('');

            // Always attempt audit API (or offline queue). Local remove rules below.
            // TicketApp #26827: never show generic "Check the connection" when
            // logItemVoid already reported offline queue or business reject.
            let voidResult = null;
            try {
                voidResult = await store.dispatch(`logItemVoid`, voidObj);
            } catch (err) {
                // Network/throw path — still remove unsaved lines; toast only for saved.
                console.error('[voidItem] logItemVoid threw', err);
                voidResult = { success: false, offline: false, businessReject: false };
            }

            if (!voidResult || !voidResult.success) {
                // salenum==0: silent success — line removed locally; BE is best-effort.
                // Why: cashier already sees the item leave; error toasts imply failure.
                if (isUnsavedCartLine) {
                    // fall through to local remove
                } else {
                    // Three-way toast contract for saved lines only (#26827):
                    // 1) offline — logItemVoid already toasted queue success/fail
                    // 2) businessReject — logItemVoid already toasted server message
                    // 3) network/unknown — show connection copy here only
                    if (!(voidResult && (voidResult.offline === true || voidResult.businessReject === true))) {
                        if (window.Toast && typeof window.Toast.fire === 'function') {
                            window.Toast.fire({
                                icon: 'error',
                                title: 'Void failed',
                                text: 'This item was not removed. Check the connection, then try again.',
                            });
                        }
                    }
                    // 22844: saved lines stay until BE accepts/queues the archive.
                    return;
                }
            }

            // Re-find by index after await — line may already be gone (double-click).
            item = state.currentSaleLines.find(s => s.index === payload.index)
            if (!item) return;

            item.Qty--;
            item.instructions = (item.instructions || []).map((s) => {
                let msg = (payload.instructions || []).find(m => m.CookingNum === s.CookingNum)
                if (msg) {
                    s.count -= msg.count
                }
                return s;
            });
            if (item.Qty <= 0) {
                let arr = state.currentSaleLines
                state.currentSaleLines = $utils.removeIf(arr, s => s.index !== payload.index)
            }
            comboDebugLog('voidItem:after-remove-before-recalc', {
                voidIndex: payload?.index,
                lines: (state.currentSaleLines || []).map(l => ({
                    index: l.index,
                    plu: l.Plu,
                    qty: Number(l.Qty) || 0,
                    unit: Number(l.Unit) || 0,
                    baseUnit: Number(l.baseUnit) || 0,
                    discount: Number(l.Discount) || 0,
                    specialType: l.specialType || null,
                })),
            });
            await store.dispatch(`calculateTipAmount`)
            store.commit(`reIndexSale`)
            store.commit('recalcSpecials')
            comboDebugLog('voidItem:after-recalc', {
                voidIndex: payload?.index,
                lines: (state.currentSaleLines || []).map(l => ({
                    index: l.index,
                    plu: l.Plu,
                    qty: Number(l.Qty) || 0,
                    unit: Number(l.Unit) || 0,
                    baseUnit: Number(l.baseUnit) || 0,
                    discount: Number(l.Discount) || 0,
                    specialType: l.specialType || null,
                })),
            });
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },

        /**
         * Void an entire cart line (PIN-gated).
         *
         * Same salenum-aware local-remove contract as voidItem (#26827 + 22844):
         * call logItemVoid for audit, always remove when salenum==0 (silent),
         * fail-closed when salenum!=0 with differentiated toasts.
         * docs: FRONTEND.md cart line identity; BE SupervisorOverrides.logItemVoid
         */
        voidLine: async (state, data) => {
            // Idempotent: already-removed index is a no-op.
            let item = state.currentSaleLines.find(s => s.index === data.index)
            if (!item) return;
            let validation = await validateSecurity(`Void`);
            if (!validation) return;

            comboDebugLog('voidLine:before', {
                voidIndex: data.index,
                lines: (state.currentSaleLines || []).map(l => ({
                    index: l.index,
                    plu: l.Plu,
                    qty: Number(l.Qty) || 0,
                    unit: Number(l.Unit) || 0,
                    baseUnit: Number(l.baseUnit) || 0,
                    discount: Number(l.Discount) || 0,
                    specialType: l.specialType || null,
                }))
            });

            let supervisor = await Storage.getSupervisor();
            const saleNum = Number(store.getters.getCurrentSale || state.currentSale || 0) || 0;
            const isUnsavedCartLine = saleNum === 0;

            // Same B5b line-id contract as voidItem — full-line void still
            // needs id so backend can archive the correct currentsale row.
            let voidObj = {
                VoidPlu: item.Plu,
                VoidDescritpiom: item.Description,
                VoidQty: item.Qty,
                VoidValue: item.Line,
                SuperVisor: supervisor,
                id: item.id || item.Id || item.lineId || item.currentSaleLineId || null,
                saleline: item.saleline != null ? item.saleline : (item.SaleLine != null ? item.SaleLine : null),
                salenum: saleNum,
                SaleNum: saleNum,
            }
            await Storage.setSupervisor('');

            // Same #26827 salenum-aware toast + remove as voidItem.
            let voidResult = null;
            try {
                voidResult = await store.dispatch(`logItemVoid`, voidObj);
            } catch (err) {
                console.error('[voidLine] logItemVoid threw', err);
                voidResult = { success: false, offline: false, businessReject: false };
            }

            if (!voidResult || !voidResult.success) {
                // salenum==0: silent local success; BE audit is best-effort (#26827).
                if (isUnsavedCartLine) {
                    // fall through to local remove
                } else {
                    if (!(voidResult && (voidResult.offline === true || voidResult.businessReject === true))) {
                        if (window.Toast && typeof window.Toast.fire === 'function') {
                            window.Toast.fire({
                                icon: 'error',
                                title: 'Void failed',
                                text: 'This item was not removed. Check the connection, then try again.',
                            });
                        }
                    }
                    return;
                }
            }

            // Second void of same index is a no-op once the first removed it.
            if (!state.currentSaleLines.find(s => s.index === data.index)) return;

            let arr = state.currentSaleLines
            state.currentSaleLines = $utils.removeIf(arr, s => {
                return s.index !== data.index;
            })
            comboDebugLog('voidLine:after-remove-before-recalc', {
                removedIndex: data.index,
                lines: (state.currentSaleLines || []).map(l => ({
                    index: l.index,
                    plu: l.Plu,
                    qty: Number(l.Qty) || 0,
                    unit: Number(l.Unit) || 0,
                    baseUnit: Number(l.baseUnit) || 0,
                    discount: Number(l.Discount) || 0,
                    specialType: l.specialType || null,
                }))
            });
            await store.dispatch(`calculateTipAmount`)
            store.commit(`reIndexSale`)
            store.commit('recalcSpecials')
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },

        addReceipt: (state, payload) => {
            state.latestInvoice = payload
            state.currentSaleLines = []
            state.saleDiscount = 0.00
        },
        setLPO: (state, payload) => {
            state.lpo = payload.lpoNumber;
            state.customer = payload.debtorID;
        },
        applyDiscount: (state, payload) => {
            // Normalize payload
            if (!payload) payload = { type: 'none', value: 0, total: 0 };
            state.saleDiscount = payload;

            // If redistribution is disabled, do not touch line discounts
            if (!state.distributeSaleLevelDiscount) {
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                return;
            }

            // Prepare helpers to compute pre-discount line totals
            const calcLineBase = (line) => {
                let total = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0));
                if (line.instructions && line.instructions.length > 0) {
                    const instructionTotal = line.instructions
                        .map(i => Number(i.Price) * Number(i.count))
                        .reduce((acc, n) => Number(acc) + Number(n), 0);
                    total += Number(instructionTotal);
                }
                return Number(total);
            };

            const lines = state.currentSaleLines || [];
            if (lines.length === 0) return;

            // Reset existing per-line discounts to avoid compounding
            let baseTotals = lines.map(line => calcLineBase({ ...line, Discount: 0 }));
            const baseSum = baseTotals.reduce((a, b) => Number(a) + Number(b), 0);
            if (baseSum <= 0) {
                // Nothing to discount
                lines.forEach((line) => {
                    line.Discount = 0;
                    line.Line = calcLineBase(line);
                });
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                return;
            }

            let targetTotalDiscount = 0;
            let ratio = 0;
            if (payload.type === 'Percentage') {
                const pct = Math.max(0, Math.min(100, Number(payload.value || 0)));
                ratio = pct / 100;
                targetTotalDiscount = Number((baseSum * ratio).toFixed(2));
            } else if (payload.type === 'Value') {
                targetTotalDiscount = Math.max(0, Math.min(Number(payload.total || payload.value || 0), baseSum));
                ratio = targetTotalDiscount / baseSum;
            } else {
                // No discount
                lines.forEach((line) => {
                    line.Discount = 0;
                    line.Line = calcLineBase(line);
                });
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                return;
            }

            // Distribute discount proportionally across lines; handle rounding residual on last line
            let accumulated = 0;
            const lastIndex = lines.length - 1;
            lines.forEach((line, i) => {
                const base = baseTotals[i];
                let lineDiscount = (i === lastIndex)
                    ? Number((targetTotalDiscount - accumulated).toFixed(2))
                    : Number((base * ratio).toFixed(2));

                // Guard rails
                if (lineDiscount < 0) lineDiscount = 0;
                if (lineDiscount > base) lineDiscount = base;

                accumulated = Number((accumulated + lineDiscount).toFixed(2));

                line.Discount = lineDiscount;
                line.Line = Number((base - lineDiscount).toFixed(2));
            });
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        clearSale: (state) => {
            state.currentSaleLines = [];
            state.currentSale = 0;
            state.saleDiscount = undefined;
            state.tip = 0;
            state.openTableName = null;
            clearOpenTableBannerState();
            state.lpo = null;
            state.customer = "";

            // Clear customer display and LED
            const ledSettings = store.state.settings || {};

            // Off-switch: see broadcastLedUpdate for rationale. When the
            // user has disabled the LED display we still want to clear
            // the customer display, but we must not touch the COM port.
            const ledDisabled = isLedDisabled(ledSettings);
            const port = ledSettings.ledPort || 'COM2';
            const baudRate = ledSettings.ledBaudRate || 2400;

            if (window.screenAPI && window.screenAPI.clearDisplay) {
                window.screenAPI.clearDisplay().catch(e => console.error(e));
            } else {
                broadcastSaleUpdate(state);
            }
            if (!ledDisabled && window.ledDisplay && window.ledDisplay.clear) {
                window.ledDisplay.clear(port, baudRate).catch(e => console.error('[LED] Clear failed:', e));
            }
        },
        clearDiscount: (state) => state.saleDiscount = undefined,
        setTipAmount: (state, data) => state.tip = data,
        setLastSaleChange: (state, payload) => {
            // Support both old format (just change amount) and new format (object with change and invoice)
            if (typeof payload === 'number') {
                state.lastSaleChange = payload;
                state.lastSaleInvoice = null;
            } else {
                state.lastSaleChange = payload.change || 0;
                state.lastSaleInvoice = payload.invoice || null;
            }
            state.showChangeNotification = true;
        },
        clearLastSaleChange: (state) => {
            state.lastSaleChange = 0;
            state.lastSaleInvoice = null;
            state.showChangeNotification = false;
        },
        clearSaleState: (state, receiptData) => {
            // Optimized mutation to clear all sale-related state in one operation
            state.currentSale = 0;
            state.tip = 0;
            state.latestInvoice = receiptData || {};
            state.saleDiscount = undefined;
            state.currentSaleLines = [];
            state.saleDetails = {};
            state.loyaltyDetails = {};
            state.openTableName = null;
            clearOpenTableBannerState();
            state.customer = "";
            state.lpo = null; // LPO is always a string (or null when cleared)

            // Broadcast final state (empty)
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        /**
         * Vuex mutation that toggles the `isSaleProcessing` flag used as a re-entry
         * guard by `processTransaction` (line 1642). Coerces any non-true value to
         * `false`, so callers must pass exactly `true` to mark the action busy.
         * The flag is consumed by `getSaleProcessingState` getter (line 546) and the
         * `isSaleProcessing` computed in `Layout.vue:41`. It is NOT bound to the
         * `:disabled` attribute of the "Pay" button, so the UI does not visibly
         * disable the button between submission and cart-clear.
         *
         * @param {Object} state Vuex state slice for sales
         * @param {boolean} payload true to mark processing, anything else to clear
         */
        setSaleProcessingState: (state, payload) => {
            state.isSaleProcessing = payload === true;
        },
    },
    actions: {
        calculateTipAmount: async (context) => {
            let settings = await Storage.getDeviceSettings();
            // Tip should be calculated from the current sale total (which already accounts for any line-level discounts)
            let saleTotal = context.getters.getSaleTotal;
            if (settings && settings.AutoTip > 0) {
                let tip = Number(settings.AutoTip);
                tip = (tip / 100) * saleTotal;
                context.commit(`setTipAmount`, tip)
            }
        },
        verifyAndLogQuantityVoid: async (context, payload) => {
            const { item, quantityToVoid } = payload;
            let validation = await validateSecurity(`Void`);
            if (!validation) return { success: false };

            let supervisor = await Storage.getSupervisor();

            // Calculate proportional value for the voided quantity
            let voidValue = (Number(item.Unit) * Number(quantityToVoid)).toFixed(2);

            let voidObj = {
                VoidPlu: item.Plu,
                VoidDescritpiom: item.Description,
                VoidQty: quantityToVoid,
                VoidValue: voidValue,
                SuperVisor: supervisor,
            }

            await Storage.setSupervisor('');
            context.dispatch(`logItemVoid`, voidObj);

            return { success: true };
        },
        /**
         * Build the JSON payload sent to the backend invoice endpoint.
         *
         * Assembles the request body that `processInvoice` later POSTs to the server
         * (or queues into the offline outbox). Of particular interest:
         *
         *  - `reference` (line ~1515): comes verbatim from `payload.reference`, which in
         *    the UI is the textarea bound in `PaymenScreen.vue:121` (`v-model="reference"`).
         *    The backend stores this in `completedsale.Reference` (and `IPosRef`-style
         *    fields). For the 2026-06-10 incident the 68 phantom rows all carried
         *    `Reference = "Sale No. <salenum>\n"`. The trailing `\n` is therefore either
         *    a keypress artifact in the textarea or a delimiter appended somewhere
         *    upstream of the UI; this function does NOT add a newline.
         *  - `localId` (line ~1522): the per-transaction UUID generated by
         *    `generateSaleUUID` in `processTransaction`. Used by the backend for
         *    idempotency on retried uploads.
         *  - `saleNumber` (line ~1508): the client-side `currentSale` value (or the
         *    offline-generated number set in `processOfflineInvoice`).
         *  - `orderNumber` (line ~1532): a separate order sequence from
         *    `OrdersService.nextOrderNumber()`.
         *
         * @param {Object} context Vuex action context
         * @param {Object} payload Raw UI values: type, total, paid, change, refund,
         *   reference, lpo, items (already in currentSaleLines), splitArray, etc.
         * @returns {Promise<Object>} Invoice payload ready to POST.
         */
         parseInvoice: async (context, payload) => {
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            let tipAmount = context.getters.getTipAmount;
            let total = Number(payload.total) - tipAmount;
            let linesDiscount = 0;
            // Drop voided/archived rows before POST. BE invoice only accepts archived=0/NULL;
            // shipping voided lines (or only voided lines) yields "no invoice lines specied".
            // docs: src/utils/activeSaleLines.js
            let items = filterActiveSaleLines(context.state.currentSaleLines);
            let discountTotal = Number(context.getters.getSaleDiscount.total);
            for (let i = 0; i < items.length; i++) {
                let line = items[i];
                linesDiscount += line.Discount;
                items[i].saleLine = i + 1;
                items[i].saleline = i + 1;
                // Net line total = ((Unit + tourismLevy) * Qty) + instructionTotal - Discount.
                // Previously this only used (Unit + tourismLevy) * Qty - Discount, which
                // dropped cooking-instruction costs and sent a `Line` of 256 instead of
                // 396 for a Pizza (Unit 300) + instruction (Price 140) with a 44 discount
                // (Felix, 2026-06-23). See getLineNetTotal JSDoc for the canonical formula.
                items[i].Line = String(getLineNetTotal(line));
                items[i].tourismLevy = (items[i].tourismLevy).toFixed(2) * Number(line.Qty);
            }
            if (context.state.lpo) {
                if (payload.paid === (total - discountTotal)) {
                    payload.paid = 0;
                }
                else if (payload.paid > (total - discountTotal)) {
                    payload.paid = payload.paid
                }
            }


            // Use the order number passed from processTransaction
            // This ensures the same order number is used for both the invoice and the printed slip
            const orderNumberCount = payload.orderNumberCount ?? payload.orderNumber ?? null;

            let invoice = {
                PC: settings.PC,
                saleNumber: context.state.currentSale,
                date: getLocalNow(),
                loyaltyBarcode: context.getters.getLoyaltyDetails.barcode,
                export: payload.type === `Export`,
                tot: context.getters.isTot,
                // Prefer Username; fail-fast — never "System" (#26328)
                user: requireSalesWriteUser(user, 'sales-module.parseInvoice'),
                lpo: (!payload.refund) ? context.state.lpo : payload.lpo,
                reference: payload.reference ? payload.reference : ``,
                customer: context.getters.getCustomer,
                paymentType: payload.type === `Export` ? `AccountSale` : payload.type,
                // Remove StockSetting from items to keep API payload clean
                // items: items.map(({ StockSetting, stockSetting, ...item }) => item),
                items: items,
                refundReason: payload.refundReason ? payload.refundReason : ``,
                refundReasonCode: payload.refundReasonCode ? payload.refundReasonCode : `06`,
                localId: context.state.saleUUID,
                invoice: (!payload.refund) ? 0 : payload.oldInv,
                discount: discountTotal,
                vat: (!payload.refund) ? parseFloat(context.getters.getSaleVat).toFixed(2) : 0,
                subTotal: (!payload.refund) ? parseFloat(context.getters.getSubTotal).toFixed(2) : parseFloat(total).toFixed(2),
                total: parseFloat(total),
                paidAmount: (!payload.refund) ? parseFloat(payload.paid).toFixed(2) : (parseFloat(total) + tipAmount).toFixed(2),
                change: (!payload.refund) ? parseFloat(payload.change).toFixed(2) : 0,
                units: (!payload.refund) ? context.getters.getSaleUnits : payload.items.length,
                tip: tipAmount,
                orderNumber: orderNumberCount,
                ...(payload.refund !== undefined && { refund: payload.refund }),
                ...(payload.refund && { rate: payload.currencyRate }),
            };

            // Attach customer details for offline receipts (Buyer Details should show even if store state is empty)
            try {
                const custId = invoice.customer;
                if (custId) {
                    let debtor = null;
                    const debtors = (store?.state?.accounts?.debtors) || [];
                    debtor = debtors.find(d => d.Id === custId) || null;

                    if (!debtor && typeof custId === 'string' && custId.startsWith('tmp_')) {
                        const offlineList = await Storage.getOfflineCustomers();
                        if (Array.isArray(offlineList)) {
                            debtor = offlineList.find(d => d.Id === custId) || null;
                        }
                    }

                    if (debtor) {
                        invoice.customerDetails = {
                            Name: debtor.Name || '',
                            Surname: debtor.Surname || '',
                            CellNumber: debtor.CellNumber || debtor.Phone || '',
                            vat: debtor.vat || debtor.TPIN || '',
                            Adress1: debtor.Adress1 || debtor.Address1 || '',
                            Area: debtor.Area || '',
                            Balance: typeof debtor.Balance === 'number' ? debtor.Balance : null
                        };
                    }
                }
            } catch (e) {
                // Non-blocking: if customer lookup fails, continue silently
            }
            if (payload.type === 'Split') {
                let splitPaidTotal = 0;
                splitPaidTotal += Number(payload.splitArray.cash || 0);
                splitPaidTotal += Number(payload.splitArray.card || 0);
                splitPaidTotal += Number(payload.splitArray.Kazang || 0);
                splitPaidTotal += Number(payload.splitArray.Spenn || 0);

                if (payload.splitArray.other) {
                    for (let i = 0; i < payload.splitArray.other.length; i++) {
                        splitPaidTotal += Number(payload.splitArray.other[i].amount || 0);
                    }
                }

                invoice.paidAmount = parseFloat(splitPaidTotal);

                // Recalculate change for split payment
                const totalToPay = invoice.total + invoice.tip;
                const change = splitPaidTotal - totalToPay;
                invoice.change = change > 0 ? parseFloat(change.toFixed(2)) : 0;

                invoice.split = [
                    {
                        method: 'cash',
                        amount: payload.splitArray.cash
                    }, {
                        method: 'card',
                        amount: payload.splitArray.card
                    },

                    {
                        method: 'Kazang',
                        amount: payload.splitArray.Kazang
                    },

                    {
                        method: 'Spenn',
                        amount: payload.splitArray.Spenn
                    },
                ];
                if (context.getters.getHasLoyalty) {
                    invoice.split.push({
                        method: 'Loyalty Program',
                        amount: payload.splitArray[`Loyalty Program`] || 0
                    })
                }
                for (let i = 0; i < payload.splitArray.other.length; i++) {
                    let method = payload.splitArray.other[i];
                    invoice.split.push({
                        method: method.name,
                        amount: method.amount
                    });
                }
            }

            if ((payload.type === 'Kazang' || (payload.type === 'Split' && payload.splitArray.Kazang > 0)) ||
                (payload.type === 'Spenn' || (payload.type === 'Split' && payload.splitArray.Spenn > 0))) {
                invoice.SecondRefrence = payload.SecondRefrence
            }

            if (payload.type === 'Kazang') {
                invoice.total += invoice.discount;
            }
            return invoice
        },
        /**
         * Top-level action invoked by the Pay button on the payment screen
         * (`PaymenScreen.vue:414` via `this.$store.dispatch("processTransaction", obj)`).
         *
         * Flow:
         *  1. Guard against re-entry using `getSaleProcessingState` (set true here, reset
         *     in the trailing `finally`). This is the ONLY in-action debounce protecting
         *     against double-click / double-tap.
         *  2. Validate security permission for the chosen payment type
         *     (Cash / Card / AccountSale / Export).
         *  3. Generate a new saleUUID (`localId` for backend idempotency) and request an
         *     order number from `OrdersService.nextOrderNumber()`.
         *  4. Call `parseInvoice` to assemble the payload, then dispatch `processInvoice`
         *     to submit it (online) or enqueue it (offline).
         *  5. On success, route to `viewPayment` (or `pos` if `hideNextSale` is on).
         *
         * Burst-relevant facts (incident 2026-06-10):
         *  - The processing flag is reset in the `finally` block (line ~1775) AFTER the
         *    full `processInvoice` promise chain resolves. If the cashier (or a retry
         *    path) triggers this action again between submission and cart-clear, the
         *    guard will correctly block the duplicate.
         *  - The PAYMENT button itself in `DesktopPaymentMethod.vue:21-29` has no
         *    `:disabled="isAmountTooLow || isSaleProcessing"` binding, so the user
         *    could double-tap before the flag is observable by the computed property.
         *  - The mobile path (`MethodsBottomSheet.vue:163`) calls `setSwipe()` on entry
         *    but does not check the processing flag.
         *  - Each invocation regenerates `saleUUID`, so the backend cannot dedupe a
         *    genuine re-entry as a retry — it will be accepted as a new sale.
         *
         * @param {Object} context Vuex action context
         * @param {Object} payload Sale UI values: type, total, paid, change, reference, items.
         * @returns {Promise<boolean>} true on success, false on guard/abort.
         */
        processTransaction: async (context, payload) => {
            if (context.getters.getSaleProcessingState) {
                console.warn('[processTransaction] Ignored duplicate trigger while processing');
                return false;
            }
            context.commit('setSaleProcessingState', true);

            try {
                let isAllowed = await validateSecurity(`convertToInvoice`);
                if (!isAllowed) return false;

                if (payload.type === `AccountSale` || payload.type === `Export`) isAllowed = await validateSecurity(`AccountSale`);
                if (!isAllowed) return false;

                if (payload.type === `Cash`) isAllowed = await validateSecurity(`Cash`);
                if (!isAllowed) return false;

                if (payload.type === `Card`) isAllowed = await validateSecurity(`Card`);
                if (!isAllowed) return false;

                // Always generate a new UUID for each sale transaction.
                // The UUID is assigned to `context.state.saleUUID` and is later placed on
                // the payload as `localId` (line 1506 of parseInvoice) and `clientSaleId`
                // (line 2027 of processInvoice). The backend uses `localId` for idempotency,
                // so a single logical sale should always carry the same UUID across retries.
                // NOTE: this UUID is regenerated every time processTransaction is called,
                // which means a re-entrant call produces a DIFFERENT UUID — and therefore
                // the backend will treat it as a different sale. This is the relevant fact
                // for the 2026-06-10 phantom-sales investigation.
                context.commit('generateSaleUUID');

                // Generate order number once for this transaction
                let orderNumberCount = null;
                try {
                    const orderNumStr = await OrdersService.nextOrderNumber();
                    orderNumberCount = parseInt(orderNumStr, 10);
                } catch (e) {
                    console.warn('[processTransaction] Failed to get order number:', e);
                }

                // Note: We don't check connectivity here because:
                // 1. The connectivity check may be unreliable (tests local favicon, not API server)
                // 2. We'll attempt the actual API call first and handle errors appropriately
                // 3. Offline mode will only be used if the API call fails with a network error
                $utils.showLoading();

                // All lines voided → do not POST empty invoice (BE 400/406 "no invoice lines specied").
                // Clear local cart so the cashier is not stuck on a dead table sale.
                // docs: src/utils/activeSaleLines.js
                const activeLines = filterActiveSaleLines(context.state.currentSaleLines);
                if (activeLines.length === 0) {
                    $utils.hideLoading();
                    context.commit('clearSale');
                    try { localStorage.removeItem('currentSaleLines'); } catch (_) { /* ignore */ }
                    await Swal.fire({
                        title: 'All items voided',
                        text: 'No items to invoice. The sale has been cleared.',
                        icon: 'success',
                    });
                    try {
                        await context.dispatch('proceedToSales');
                    } catch (e) {
                        console.warn('[processTransaction] proceedToSales after all-voided:', e);
                    }
                    return true;
                }

                let invoice = await context.dispatch('parseInvoice', { ...payload, orderNumberCount })
                return context.dispatch('processInvoice', invoice)
                    .then(async res => {
                        if (res) {
                            // Check hideNextSale setting
                            const settings = await Storage.getDeviceSettings();

                            // Get the change amount and invoice from the latest receipt
                            const changeAmount = Number(invoice?.change) || 0;
                            const latestReceipt = context.getters.getLatestInvoice;
                            const invoiceNumber = latestReceipt?.invoice || latestReceipt?.saleId || latestReceipt?.id || null;

                            if (settings?.hideNextSale === true) {

                                context.commit('setLastSaleChange', {
                                    change: changeAmount,
                                    invoice: invoiceNumber
                                });

                                // Update LED display based on user's selected value setting
                                if (window.ledDisplay && window.ledDisplay.showWithMode) {
                                    const ledSettings = store.state.settings || {};

                                    // Off-switch: see broadcastLedUpdate for rationale.
                                    if (isLedDisabled(ledSettings)) {
                                        console.log('[LED] Sale-complete display skipped: LED display disabled in settings');
                                    } else {
                                    const port = ledSettings.ledPort || 'COM2';
                                    const baudRate = ledSettings.ledBaudRate || 2400;
                                    const displayValue = ledSettings.ledDisplayValue || 'SALE_TOTAL';
                                    const paidAmount = Number(invoice?.paidAmount) || 0;
                                    const saleTotal = Number(invoice?.total) || 0;
                                    console.log(`[LED] Sale complete - Display: ${displayValue}, Total: ${saleTotal}, Payment: ${paidAmount}, Change: ${changeAmount}, Port: ${port}, Baud: ${baudRate}`);

                                    // Show the value selected by user in settings
                                    switch (displayValue) {
                                        case 'PAID_AMOUNT':
                                            if (paidAmount > 0) {
                                                console.log(`[LED] Displaying payment: ${paidAmount.toFixed(2)}`);
                                                window.ledDisplay.showWithMode('payment', paidAmount.toFixed(2), port, baudRate)
                                                    .catch(e => console.error('[LED] Payment display failed:', e));
                                            }
                                            break;
                                        case 'CHANGE':
                                            if (changeAmount > 0) {
                                                console.log(`[LED] Displaying change: ${changeAmount.toFixed(2)}`);
                                                window.ledDisplay.showWithMode('change', changeAmount.toFixed(2), port, baudRate)
                                                    .catch(e => console.error('[LED] Change display failed:', e));
                                            }
                                            break;
                                        case 'SALE_TOTAL':
                                        default:
                                            console.log(`[LED] Displaying total: ${saleTotal.toFixed(2)}`);
                                            window.ledDisplay.showWithMode('total', saleTotal.toFixed(2), port, baudRate)
                                                .catch(e => console.error('[LED] Total display failed:', e));
                                            break;
                                    }
                                    }
                                }

                                // Route to POS screen
                                return router.push({ name: 'pos' });
                            } else {
                                // Update LED display based on user's selected value setting before routing
                                if (window.ledDisplay && window.ledDisplay.showWithMode) {
                                    const ledSettings = store.state.settings || {};

                                    // Off-switch: see broadcastLedUpdate for rationale.
                                    if (isLedDisabled(ledSettings)) {
                                        console.log('[LED] Sale-complete (viewPayment) display skipped: LED display disabled in settings');
                                    } else {
                                    const port = ledSettings.ledPort || 'COM2';
                                    const baudRate = ledSettings.ledBaudRate || 2400;
                                    const displayValue = ledSettings.ledDisplayValue || 'SALE_TOTAL';
                                    const paidAmount = Number(invoice?.paidAmount) || 0;
                                    const saleTotal = Number(invoice?.total) || 0;
                                    console.log(`[LED] Sale complete (viewPayment) - Display: ${displayValue}, Total: ${saleTotal}, Payment: ${paidAmount}, Change: ${changeAmount}, Port: ${port}, Baud: ${baudRate}`);

                                    // Show the value selected by user in settings
                                    switch (displayValue) {
                                        case 'PAID_AMOUNT':
                                            if (paidAmount > 0) {
                                                console.log(`[LED] Displaying payment: ${paidAmount.toFixed(2)}`);
                                                window.ledDisplay.showWithMode('payment', paidAmount.toFixed(2), port, baudRate)
                                                    .catch(e => console.error('[LED] Payment display failed:', e));
                                            }
                                            break;
                                        case 'CHANGE':
                                            if (changeAmount > 0) {
                                                console.log(`[LED] Displaying change: ${changeAmount.toFixed(2)}`);
                                                window.ledDisplay.showWithMode('change', changeAmount.toFixed(2), port, baudRate)
                                                    .catch(e => console.error('[LED] Change display failed:', e));
                                            }
                                            break;
                                        case 'SALE_TOTAL':
                                        default:
                                            console.log(`[LED] Displaying total: ${saleTotal.toFixed(2)}`);
                                            window.ledDisplay.showWithMode('total', saleTotal.toFixed(2), port, baudRate)
                                                .catch(e => console.error('[LED] Total display failed:', e));
                                            break;
                                    }
                                    }
                                }

                                // Default behavior: route to viewPayment
                                return router.push({ name: 'viewPayment' });
                            }
                        }
                    }).finally(() => $utils.hideLoading())
            } finally {
                context.commit('setSaleProcessingState', false);
            }
        },

        // processInvoice: async (context, payload) => {
        //     const [store, currency, user] = await Promise.all([
        //         Storage.getDefaultStore(),
        //         Storage.getBaseCurrency(),
        //         Storage.getCurrentUser()
        //     ]);
        //
        //     // Generate unique client sale ID for idempotency
        //     const clientSaleId = generateUUID();
        //     const enhancedPayload = {
        //         ...payload,
        //         clientSaleId,
        //         clientTimestamp: new Date().toISOString()
        //     };
        //
        //     // Always attempt online processing first
        //     // Don't rely on navigator.onLine as it can incorrectly report offline status
        //     // The axios call will naturally fail and fallback to offline mode if server is unreachable
        //     // Attempt to post to server
        //     return axios.post(serverApi + `invoice/${store.StoreDB}/mobile?android=true`, enhancedPayload, { timeout: 20000 })
        //         .then(async ({ data }) => {
        //             if (typeof data === 'string') {
        //                 let correctedData = data.replaceAll(/NaN/g, '0');
        //                 data = JSON.parse(correctedData);
        //             }
        //             if (data.status === 200) {
        //                 try {
        //                     // Send Payment Success to Customer Display
        //                     if (window.screenAPI && window.screenAPI.sendPaymentData) {
        //                         // Get currency from store
        //                         const displayCurrency = store.getters.getBaseCurrency || { Symbol: 'K', Name: 'Kwacha' };
        //
        //                         const paymentData = {
        //                             grandTotal: enhancedPayload.total,
        //                             change: enhancedPayload.change,
        //                             method: enhancedPayload.paymentType || 'Cash',
        //                             currency: displayCurrency
        //                         };
        //
        //                         console.log('[CustomerDisplay] Sending payment summary:', paymentData);
        //                         window.screenAPI.sendPaymentData(paymentData).catch(e => console.error('[CustomerDisplay] Payment send failed:', e));
        //                     }
        //                 } catch (e) {
        //                     console.error('Error processing invoice response:', e);
        //                 }
        //                 // Start printing in background (non-blocking)
        //                 // This allows immediate sale finalization without waiting for print to complete
        //                 try {
        //                     const settings = await Storage.getDeviceSettings();
        //                     const shouldPrint = settings?.PrintReceipt !== false; // default true
        //                     const shouldPrompt = settings?.PromptA4Print === true; // default false
        //
        //                     if (shouldPrint) {
        //                         let doPrint = true;
        //                         if (shouldPrompt) {
        //                             const res = await Swal.fire({
        //                                 title: 'Print receipt?',
        //                                 text: 'Would you like to print the receipt now?',
        //                                 icon: 'question',
        //                                 showCancelButton: true,
        //                                 confirmButtonText: 'Print',
        //                                 cancelButtonText: 'Skip',
        //                                 reverseButtons: true,
        //                             });
        //                             doPrint = res.isConfirmed === true;
        //                         }
        //
        //                         if (doPrint) {
        //                             // Fire-and-forget: print in background without blocking
        //                             context.dispatch(`BtPrint`, {
        //                                 invoice: data.response.invoice,
        //                                 reprint: false
        //                             }).catch(err => {
        //                                 console.error('Print failed (non-blocking):', err);
        //                                 // Optional: show non-intrusive notification
        //                                 Toast.fire({
        //                                     icon: 'warning',
        //                                     title: 'Print Failed',
        //                                     text: 'Receipt not printed. You can reprint from sales history.'
        //                                 });
        //                             });
        //                         }
        //                     }
        //                 } catch (e) {
        //                     console.error('Print setup failed:', e);
        //                 }
        //
        //                 // Clear sale state immediately (don't wait for print)
        //                 // Use optimized consolidated mutation instead of Promise.all
        //                 context.commit('clearSaleState', data.response);
        //                 localStorage.removeItem('currentSaleLines');
        //                 Swal.close();
        //
        //                 return true;
        //             }
        //         }).catch(async (err) => {
        //             // Only fallback to offline mode for timeout or network errors
        //             // For other errors (4xx, 5xx), propagate them normally
        //
        //             const isTimeout = err.code === 'ECONNABORTED' ||
        //                 (err.message && err.message.toLowerCase().includes('timeout'));
        //             const isNetworkError = err.code === 'ERR_NETWORK' ||
        //                 !err.response ||
        //                 err.message === 'Network Error';
        //
        //             // If it's an HTTP error with a status code (not a network/timeout issue), don't fallback to offline
        //             if (!isTimeout && !isNetworkError && err.response) {
        //                 console.error('Server returned an error (not a connectivity issue):', err);
        //                 return errorHandler.tomcatError(err);
        //             }
        //
        //             console.warn('Invoice posting failed due to timeout or network issue, falling back to offline mode:', err);
        //
        //             try {
        //                 const isOnline = await connectivityMonitor.forceCheck();
        //                 const settings = await Storage.getDeviceSettings();
        //
        //                 const flag = settings.allowOfflineInvoicing;
        //                 if (!flag) {
        //                     try {
        //                         await Swal.fire({
        //                             title: 'Sale Vault Disabled',
        //                             text: 'This device is offline and Sale Vault is disabled. Please connect to the internet to process this sale.',
        //                             icon: 'warning',
        //                             confirmButtonText: 'OK'
        //                         });
        //                     } catch (e) {
        //                     }
        //                     $utils.hideLoading();
        //                     return Promise.reject(new Error('Offline invoicing is disabled. Sale cannot be processed while offline.'));
        //                 }
        //
        //                 // Enforce offline time limit based on system settings
        //                 try {
        //                     const sys = (store && store.getters && store.getters.getSystemSettings) ? store.getters.getSystemSettings : await Storage.getSystemSettings();
        //                     const hours = (sys && (sys.maxOfflineHours || sys.MaxOfflineHours)) || 3;
        //                     const remainingMs = connectivityMonitor.getRemainingOfflineTime(Number(hours) * 60 * 60 * 1000);
        //                     if (remainingMs === 0) {
        //                         try {
        //                             await Swal.fire({
        //                                 title: 'Offline Limit Reached',
        //                                 text: 'You can no longer continue with offline sales. Please connect to the internet to proceed.',
        //                                 icon: 'warning',
        //                                 confirmButtonText: 'OK'
        //                             });
        //                         } catch (e) { }
        //                         $utils.hideLoading();
        //                         return Promise.reject(new Error('Offline limit reached. Please reconnect to continue.'));
        //                     }
        //                 } catch (e) {
        //                     // If any error occurs, proceed but log it
        //                     console.warn('Failed to evaluate offline time limit:', e);
        //                 }
        //
        //                 const saleId = await outbox.enqueueSale(
        //                     enhancedPayload,
        //                     store.StoreDB,
        //                     {
        //                         cashierName: user?.Username || 'Unknown',
        //                         cashierId: user?.UserId || null,
        //                         appVersion: '1.0.0',
        //                         failedOnlineAttempt: true,
        //                         error: err.message
        //                     }
        //                 );
        //
        //                 // Check if outbox store is available
        //                 if (saleId === null) {
        //                     console.warn('Outbox store not available. Sale cannot be queued for sync.');
        //                     Toast.fire({
        //                         icon: 'error',
        //                         title: 'Database Issue',
        //                         text: 'Unable to save sale. Please refresh the page or contact support.'
        //                     });
        //                     return errorHandler.tomcatError({ message: 'Database store not initialized' });
        //                 }
        //
        //                 // Create provisional receipt data using the generated saleId
        //                 const provisionalReceipt = {
        //                     invoice: saleId,
        //                     clientSaleId,
        //                     saleId,
        //                     provisional: true,
        //                     offline: true,
        //                     total: payload.total || 0,
        //                     paidAmount: payload.paidAmount || 0,
        //                     change: payload.change || 0,
        //                     timestamp: new Date().toISOString()
        //                 };
        //
        //                 // Print provisional receipt if possible
        //                 try {
        //                     await context.dispatch('printProvisionalReceipt', {
        //                         receipt: provisionalReceipt,
        //                         payload: enhancedPayload
        //                     });
        //                 } catch (printErr) {
        //                     console.error('Failed to print provisional receipt:', printErr);
        //                 }
        //
        //                 // Show notification
        //                 Toast.fire({
        //                     icon: 'warning',
        //                     title: 'Connection Issue',
        //                     text: 'Sale queued and will sync automatically'
        //                 });
        //
        //                 // Clear sale state immediately using optimized mutation
        //                 context.commit('clearSaleState', provisionalReceipt);
        //                 localStorage.removeItem('currentSaleLines');
        //                 Swal.close();
        //
        //                 return true;
        //             } catch (outboxErr) {
        //                 console.error('Failed to enqueue sale after network error:', outboxErr);
        //                 return errorHandler.tomcatError(err);
        //             }
        //         })
        // },

        refreshSoldItemsQtyAfterSale: async (context, { items, storeDb } = {}) => {
            const soldLines = Array.isArray(items) ? items : [];
            const pluList = Array.from(new Set(
                soldLines
                    .map(line => String(line?.Plu ?? line?.plu ?? line?.PLU ?? '').trim())
                    .filter(Boolean)
            ));

            if (pluList.length === 0) return [];

            const db = storeDb || await getStoreDB();
            if (!db) return [];

            const [settings, currentUser] = await Promise.all([
                Storage.getDeviceSettings(),
                Storage.getCurrentUser()
            ]);
            const pc = settings?.PC || '';
            const user = currentUser?.Username || currentUser?.UserName || '';

            let refreshedItems = [];
            try {
                const response = await axios.post(
                    serverApi + `stock/${db}/plus`,
                    pluList,
                    {
                        params: { pc, user },
                        timeout: 10000
                    }
                );

                const payload = response?.data?.response ?? response?.data ?? [];
                const stockItems = Array.isArray(payload) ? payload : [];
                refreshedItems = stockItems.filter(item => item && item.PLU);
            } catch (err) {
                console.warn('[Sales] Failed to refresh stock qty via /plus endpoint:', err);
                return [];
            }

            if (refreshedItems.length > 0) {
                await context.dispatch('applyServerStockUpdates', refreshedItems);
            }

            return refreshedItems;
        },

        /**
         * Submit a sale invoice to the backend.
         *
         * The entry point for committing a sale. Behavior:
         *  1. Generates a fresh `clientSaleId` (UUID) and `clientTimestamp` for idempotency.
         *  2. If `connectivityMonitor` reports offline, falls back to `processOfflineInvoice`
         *     which enqueues the sale into the IndexedDB/SQLite outbox — except when
         *     `payload.refund === true` (refunds must hit the server; offline is rejected).
         *  3. Otherwise, POSTs the invoice to `serverApi/invoice/{storeDB}/mobile?android=true`
         *     with a 30s timeout. On success: clears the cart via `clearSaleState` and runs
         *     background printing/stock refresh.
         *  4. On timeout / network error: falls back to `processOfflineInvoice` for normal
         *     sales only (NOT for refunds, and NOT for HTTP error status codes — those are
         *     surfaced to the cashier).
         *
         * NOTE for incident 2026-06-10 (Anathoth, 68 phantom sales): the action itself has
         * no in-action debounce. The guard is implemented by `setSaleProcessingState` in
         * `processTransaction` (line 1615). If `processTransaction` is re-entered before
         * the cart-clearing mutations propagate to the UI, multiple submissions can
         * theoretically race. See `docs/exec-plans/active/2026-06-11-phantom-sales-burst-anathoth.md`.
         *
         * @param {Object} context Vuex action context
         * @param {Object} payload Parsed invoice payload from `parseInvoice` (see line 1460).
         * @returns {Promise<boolean>} true on success
         */
        processInvoice: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let currency = await Storage.getBaseCurrency();
            let user = await Storage.getCurrentUser();
            let deviceSettings = await Storage.getDeviceSettings();

            // Defense in depth: never POST voided/empty item sets (parseInvoice also filters).
            // docs: src/utils/activeSaleLines.js
            const activeItems = filterActiveSaleLines(payload && payload.items);
            if (activeItems.length === 0) {
                context.commit('clearSale');
                try { localStorage.removeItem('currentSaleLines'); } catch (_) { /* ignore */ }
                $utils.hideLoading();
                await Swal.fire({
                    title: 'All items voided',
                    text: 'No items to invoice. The sale has been cleared.',
                    icon: 'success',
                });
                try {
                    await context.dispatch('proceedToSales');
                } catch (e) {
                    console.warn('[processInvoice] proceedToSales after all-voided:', e);
                }
                return true;
            }

            // Generate unique client sale ID for idempotency
            const clientSaleId = generateUUID();
            const enhancedPayload = {
                ...payload,
                items: activeItems,
                clientSaleId,
                clientTimestamp: new Date().toISOString()
            };

            // Refunds reverse a server-side invoice; queuing them offline would desync stock/credit notes.
            const isRefund = enhancedPayload.refund === true;

            // Check connectivity before making API call
            const isOnline = connectivityMonitor.getStatus();
            if (!isOnline) {
                if (isRefund) {
                    console.warn('[Invoice] Refund blocked: device offline (refunds cannot use Sale Vault)');
                    $utils.hideLoading();
                    try {
                        await Swal.fire({
                            title: 'Internet required',
                            text: 'Refunds require an internet connection and cannot be processed offline. Please reconnect and try again.',
                            icon: 'warning',
                            confirmButtonText: 'OK'
                        });
                    } catch (e) { /* ignore Swal dismiss */ }
                    return Promise.reject(new Error('Refunds cannot be processed offline. Please reconnect and try again.'));
                }
                console.log('[Invoice] Device is offline, will queue sale for later sync');
                // Fall directly to offline mode without attempting API call
                return context.dispatch('processOfflineInvoice', { enhancedPayload, store, user, clientSaleId });
            }

            // Attempt to post to server
            return axios.post(serverApi + `invoice/${store.StoreDB}/mobile?android=true`, enhancedPayload, { timeout: 30000 })
                .then(async ({ data }) => {
                    if (typeof data === 'string') {
                        let correctedData = data.replaceAll(/NaN/g, '0');
                        data = JSON.parse(correctedData);
                    }
                    if (data.status === 200) {
                        const responseData = data?.response || {};
                        const invoiceNumber = responseData?.invoice ?? responseData?.Invoice;

                        // Finalize and clear sale immediately once server confirms success.
                        context.commit('clearSaleState', responseData);
                        context.dispatch('setCustomer', ''); // Clear customer after sale
                        localStorage.removeItem('currentSaleLines');
                        Swal.close();

                        // Continue print flow in background so UI routing is not blocked.
                        (async () => {
                            try {
                                const shouldPrint = deviceSettings?.PrintReceipt !== false; // default true
                                const shouldPrompt = deviceSettings?.PromptA4Print === true; // default false
                                if (shouldPrint) {
                                    let doPrint = true;
                                    if (shouldPrompt) {
                                        const res = await Swal.fire({
                                            title: 'Print receipt?',
                                            text: 'Would you like to print the receipt now?',
                                            icon: 'question',
                                            showCancelButton: true,
                                            confirmButtonText: 'Print',
                                            cancelButtonText: 'Skip'
                                        });
                                        doPrint = res.isConfirmed === true;
                                    }
                                    if (doPrint) {
                                        await context.dispatch(`BtPrint`, { invoice: invoiceNumber ?? responseData?.invoice, reprint: false });
                                    }
                                }
                            } catch (e) {
                                console.warn('[Online Print] Prompt/dispatch failed:', e);
                            }

                            // Local per-item order printing on sale (online)
                            try {
                                console.log('[Sales] Starting order slip print (online)');
                                const lines = Array.isArray(enhancedPayload?.items) ? enhancedPayload.items : [];
                                // Enrich items with StockSetting if missing
                                for (const line of lines) {
                                    if (!line.StockSetting && !line.stockSetting) {
                                        try {
                                            const setting = await Storage.getStockSetting(line.Plu || line.plu);
                                            if (setting) line.StockSetting = setting;
                                        } catch (e) { console.warn('Failed to fetch stock setting for print', e); }
                                        if (!line.StockSetting && store?.getters?.getByPlu) {
                                            const stockItem = store.getters.getByPlu(line.Plu || line.plu);
                                            if (stockItem?.StockSetting) line.StockSetting = stockItem.StockSetting;
                                        }
                                    }
                                    if (!line.StockSetting && !line.stockSetting) {
                                        console.warn('[Sales] Missing StockSetting for order slip (online)', line.Plu || line.plu);
                                    }
                                }
                                const responseSaleNumber = responseData?.saleNumber ?? responseData?.SaleNum ?? responseData?.saleNum;
                                await OrdersService.printOrder(lines, {
                                    tableName: 'Quick Sale',
                                    orderNumber: invoiceNumber ?? enhancedPayload.orderNumber,
                                    saleNumber: responseSaleNumber ?? enhancedPayload.saleNumber
                                });
                            } catch (e) {
                                console.warn('[Sales] Local order print failed (online):', e);
                            }
                        })();

                        context.dispatch('refreshSoldItemsQtyAfterSale', {
                            items: enhancedPayload?.items,
                            storeDb: store.StoreDB
                        }).then((refreshed) => {
                            if (Array.isArray(refreshed) && refreshed.length > 0) {
                                console.log(`[Sales] Refreshed stock qty for ${refreshed.length} sold item(s)`);
                            }
                        }).catch((e) => {
                            console.warn('[Sales] Post-sale stock refresh failed:', e);
                        });

                        return true;
                    }
                }).catch(async (err) => {
                    // Only fallback to offline mode for timeout or network errors
                    // For other errors (4xx, 5xx), propagate them normally

                    const isTimeout = err.code === 'ECONNABORTED' ||
                        (err.message && err.message.toLowerCase().includes('timeout'));
                    const isNetworkError = err.code === 'ERR_NETWORK' ||
                        !err.response ||
                        err.message === 'Network Error';

                    // If it's an HTTP error with a status code (not a network/timeout issue), don't fallback to offline
                    if (!isTimeout && !isNetworkError && err.response) {
                        console.error('Server returned an error (not a connectivity issue):', err);
                        return errorHandler.tomcatError(err);
                    }

                    // Refunds must never enter the outbox — even if the POST dies mid-flight after going offline.
                    if (isRefund) {
                        console.warn('[Invoice] Refund blocked: network/timeout (no offline fallback):', err);
                        $utils.hideLoading();
                        try {
                            await Swal.fire({
                                title: 'Internet required',
                                text: 'Refunds require an internet connection and cannot be processed offline. Please reconnect and try again.',
                                icon: 'warning',
                                confirmButtonText: 'OK'
                            });
                        } catch (e) { /* ignore Swal dismiss */ }
                        return Promise.reject(new Error('Refunds cannot be processed offline. Please reconnect and try again.'));
                    }

                    console.warn('Invoice posting failed due to timeout or network issue, falling back to offline mode:', err);

                    // Use the offline invoice handler
                    return context.dispatch('processOfflineInvoice', { enhancedPayload, store, user, clientSaleId, originalError: err.message });
                })
        },

        /**
         * Enqueue a sale into the local outbox when the backend is unreachable.
         *
         * Reached either because the connectivity monitor reported offline up front
         * (processInvoice, line ~2128) or because the online POST failed with a
         * network/timeout error (processInvoice .catch, line ~2225).
         *
         * Key steps:
         *  1. Reads `settings.allowOfflineInvoicing` (Sale Vault). If disabled, aborts
         *     with a user-facing warning.
         *  2. Generates an 8-digit `offlineSaleNumber` from `Date.now() % 1e8` and
         *     assigns it to `enhancedPayload.saleNumber` and `enhancedPayload.orderNumber`
         *     (lines ~2261-2263).
         *  3. Calls `outbox.enqueueSaleWithStockMutation` to write the sale to local
         *     IndexedDB/SQLite AND atomically decrement local stock and mark stock
         *     "dirty pending" for that saleId.
         *  4. Prints a provisional receipt and per-item order slips, then clears the
         *     cart via the same set of mutations that the online path uses.
         *
         * Burst-relevant facts:
         *  - Each call generates a NEW `offlineSaleNumber` (a fresh timestamp). There
         *    is no in-action debounce here; the only debounce is in `processTransaction`
         *    (line 1642).
         *  - `clientSaleId` is preserved from `processInvoice` so retried enqueues
         *    during a single transaction share the same UUID. But across separate
         *    processTransaction invocations (e.g. re-entrancy), a NEW UUID is generated
         *    upstream.
          *  - Outbox uniqueness is (sale_id, store_db). sale_id stays offline-{pc}-{n}
          *    from that store's sequence. enqueueSaleWithStockMutation retries on
          *    collision via getNextInvoiceNumber(storeDb).
         *  - When connectivity returns, `SyncService._syncItem` POSTs each outbox row
         *    to the server. The backend then assigns a fresh `salenum` (the 56-offset
         *    `InvNum = salenum - 56` matches the store's `invnum` table behaviour).
         *
         * @param {Object} context Vuex action context
         * @param {Object} args
         * @param {Object} args.enhancedPayload Invoice payload augmented with `clientSaleId`.
         * @param {Object} args.store           Cached `getDefaultStore()` result.
         * @param {Object} args.user            Cached `getCurrentUser()` result.
         * @param {string} args.clientSaleId    UUID generated in processInvoice (line ~2027).
         * @param {string} [args.originalError] Network error message if this was a fallback.
         * @returns {Promise<boolean>} true on success.
         */
        processOfflineInvoice: async (context, { enhancedPayload, store, user, clientSaleId, originalError }) => {
            try {
                // Defense in depth: processInvoice already blocks refunds; reject any direct caller too.
                if (enhancedPayload && enhancedPayload.refund === true) {
                    $utils.hideLoading();
                    try {
                        await Swal.fire({
                            title: 'Internet required',
                            text: 'Refunds require an internet connection and cannot be processed offline. Please reconnect and try again.',
                            icon: 'warning',
                            confirmButtonText: 'OK'
                        });
                    } catch (e) { /* ignore Swal dismiss */ }
                    return Promise.reject(new Error('Refunds cannot be processed offline. Please reconnect and try again.'));
                }

                const settings = await Storage.getDeviceSettings();

                const flag = settings.allowOfflineInvoicing;
                if (!flag) {
                    try {
                        await Swal.fire({
                            title: 'Sale Vault Disabled',
                            text: 'This device is offline and Sale Vault is disabled. Please connect to the internet to process this sale.',
                            icon: 'warning',
                            confirmButtonText: 'OK'
                        });
                    } catch (e) {
                    }
                    $utils.hideLoading();
                    return Promise.reject(new Error('Offline invoicing is disabled. Sale cannot be processed while offline.'));
                }

                const timestamp8 = Date.now() % 100000000;
                const offlineSaleNumber = timestamp8 < 10000000 ? timestamp8 + 10000000 : timestamp8;
                enhancedPayload.saleNumber = offlineSaleNumber;
                enhancedPayload.orderNumber = offlineSaleNumber;

                const enqueueResult = await outbox.enqueueSaleWithStockMutation(
                    enhancedPayload,
                    store.StoreDB,
                    {
                        cashierName: user?.Username || 'Unknown',
                        cashierId: user?.UserId || null,
                        appVersion: '1.0.0',
                        failedOnlineAttempt: !!originalError,
                        error: originalError || 'Offline mode'
                    }
                );
                const saleId = typeof enqueueResult === 'string' ? enqueueResult : enqueueResult?.saleId;
                const localStockUpdates = Array.isArray(enqueueResult?.stockUpdates) ? enqueueResult.stockUpdates : [];

                // Enqueue failed (e.g. sale_id collision after retries, storage write error)
                if (saleId === null || saleId === undefined) {
                    console.warn('Failed to enqueue offline sale. Sale cannot be queued for sync.');
                    Toast.fire({
                        icon: 'error',
                        title: 'Offline Save Failed',
                        text: 'Unable to queue this sale for sync. Please try again.'
                    });
                    return errorHandler.tomcatError({ message: 'Unable to queue offline sale for sync' });
                }

                if (localStockUpdates.length > 0) {
                    context.commit('mergeServerStockItems', localStockUpdates);
                }
                await Storage.invalidateStockCache();

                // Create provisional receipt data using the generated saleId
                const provisionalReceipt = {
                    invoice: saleId,
                    saleNumber: offlineSaleNumber,
                    clientSaleId,
                    saleId,
                    storeDB: store?.StoreDB || store?.storeDB || '',
                    provisional: true,
                    offline: true,
                    reference: enhancedPayload.reference || '',
                    total: enhancedPayload.total || 0,
                    paidAmount: enhancedPayload.paidAmount || 0,
                    change: enhancedPayload.change || 0,
                    timestamp: new Date().toISOString()
                };

                // Print provisional receipt if possible
                try {
                    await context.dispatch('printProvisionalReceipt', {
                        receipt: provisionalReceipt,
                        payload: enhancedPayload
                    });
                } catch (printErr) {
                    console.error('Failed to print provisional receipt:', printErr);
                }

                // Local per-item order printing on sale (offline)
                try {
                    console.log('[Sales] Starting order slip print (offline)');
                    const lines = Array.isArray(enhancedPayload?.items) ? enhancedPayload.items : [];
                    // Enrich items with StockSetting if missing
                    for (const line of lines) {
                        if (!line.StockSetting && !line.stockSetting) {
                            try {
                                const setting = await Storage.getStockSetting(line.Plu || line.plu);
                                if (setting) line.StockSetting = setting;
                            } catch (e) { console.warn('Failed to fetch stock setting for print', e); }
                            if (!line.StockSetting && store?.getters?.getByPlu) {
                                const stockItem = store.getters.getByPlu(line.Plu || line.plu);
                                if (stockItem?.StockSetting) line.StockSetting = stockItem.StockSetting;
                            }
                        }
                        if (!line.StockSetting && !line.stockSetting) {
                            console.warn('[Sales] Missing StockSetting for order slip (offline)', line.Plu || line.plu);
                        }
                    }
                    await OrdersService.printOrder(lines, {
                        tableName: 'Quick Sale',
                        orderNumber: enhancedPayload.orderNumber,
                        saleNumber: enhancedPayload.saleNumber
                    });
                } catch (e) {
                    console.warn('[Sales] Local order print failed (offline):', e);
                }

                // Show notification
                Toast.fire({
                    icon: 'warning',
                    title: 'Connection Issue',
                    text: 'Sale queued and will sync automatically'
                });

                // Clear sale and proceed
                return Promise.all([
                    context.commit('setCurrentSale', 0),
                    context.commit('setTipAmount', 0),
                    context.commit('addReceipt', provisionalReceipt),
                    context.commit('clearDiscount'),
                    context.commit('setSaleDetails', {}),
                    context.commit('setLoyaltyDetails', {}),
                    context.commit('setCurrentSale', 0),
                    context.commit('setOpenTableName', null),
                    context.dispatch('setCustomer', ""),
                    context.commit('setCurrentLines', []),
                    context.commit('setLPO', {
                        debtorID: ``,
                        lpoNumber: undefined,
                    }),
                ]).then(() => {
                    // Prevent stale sale lines from reloading on next POS mount
                    localStorage.removeItem('currentSaleLines');
                    Swal.close();
                    return true;
                });
            } catch (outboxErr) {
                console.error('Failed to enqueue sale after network error:', outboxErr);
                return errorHandler.tomcatError(outboxErr);
            }
        },

        /**
         * Builds and dispatches an ESC/POS receipt for a sale that was queued to the
         * outbox (offline path). The receipt mirrors the on-screen cart items, applies
         * the same `SPECIAL`/discount formatting used by the online receipt, and writes
         * per-item cooking/special instructions beneath each line so the kitchen and
         * customer both see them on the printed slip.
         *
         * @param {Object} context      Vuex action context.
         * @param {Object} args
         * @param {Object} args.receipt Provisional receipt metadata (invoice number, total, paid, change, timestamp).
         * @param {Object} args.payload Parsed invoice payload from `parseInvoice`. `payload.items` are the raw
         *                              cart line objects and include any `instructions` attached to the line.
         * @returns {Promise<boolean>}  `true` once printing finishes (or is intentionally skipped).
         */
        /**
         * Renders ESC/POS commands for offline provisional receipts (initial offline
         * sale and Offline Invoices reprint). Reads VAT values from the payload but
         * may derive print-only primitives (printVat, printExclTotal, lineVat) when
         * the selected store's onVat flag is false. MUST NOT mutate the payload or
         * its items — the same payload is enqueued to the outbox and synced to the
         * server with full VAT intact.
         * @param {Object} context Vuex action context (uses rootGetters)
         * @param {Object} args { receipt, payload }
         */
        printProvisionalReceipt: async (context, { receipt, payload }) => {
            console.log("receipt",receipt)
            console.log("payload",payload)
            // Print provisional receipt for offline sales
            // This creates a proper ESC/POS formatted receipt with "OFFLINE" branding
            try {
                const [store, settings] = await Promise.all([
                    Storage.getRegistrationDetails(),
                    Storage.getDeviceSettings()
                ]);

                const shouldPrint = settings?.PrintReceipt !== false; // default true
                const shouldPrompt = settings?.PromptA4Print === true; // default false

                // Determine line width based on ReceiptType (58mm vs 80mm)
                const receiptType = settings?.ReceiptType || 'Normal';
                const is55mm = receiptType === '55ml' || receiptType.includes('55');
                const lineWidth = is55mm ? 32 : 48;

                if (!shouldPrint) {
                    return true; // Skip printing silently
                }
                if (shouldPrompt) {
                    const res = await Swal.fire({
                        title: 'Print receipt?',
                        text: 'Would you like to print the receipt now?',
                        icon: 'question',
                        showCancelButton: true,
                        confirmButtonText: 'Print',
                        cancelButtonText: 'Skip',
                        reverseButtons: true,
                    });
                    if (!res.isConfirmed) return true;
                }

                // Map invoice structure to receipt structure
                // payload has: items (array), vat, subTotal, total, paidAmount, change, units, paymentType
                const invoiceLines = payload.items || [];
                const exclTotal = Number(payload.subTotal || 0);
                const taxTotal = Number(payload.vat || 0);
                const total = Number(payload.total || receipt.total || 0);
                // Print-side gate: derive VAT-print values from store.onVat.
                // These are PRINTSIDE ONLY — the payload/outbox keeps full VAT for sync.
                const onVat = context.rootGetters.getOnVatStatus === true;
                const printVat = onVat ? Number(payload.vat || 0) : 0;
                const printExclTotal = onVat ? Number(payload.subTotal || 0) : total;
                const paidAmount = Number(payload.paidAmount || receipt.paidAmount || 0);
                const change = Number(paidAmount - total);
                const paymentType = payload.paymentType || 'Cash';
                const totalUnits = Number(payload.units || 0);
                const reference = String(payload.reference || receipt.reference || '').trim();

                // Helper to generate ESC/POS commands
                const ESC = 0x1B;
                const GS = 0x1D;

                // Build ESC/POS command buffer
                const commands = [];

                // Initialize printer
                commands.push(ESC, 0x40);

                // Store name - Bold, Center, Large
                commands.push(ESC, 0x61, 0x01); // Center align
                commands.push(ESC, 0x45, 0x01); // Bold on
                commands.push(GS, 0x21, 0x11); // Double width & height
                console.log('[Offline Print] store:', store);
                console.log('[Offline Print] settings:', settings);
                const storeName = settings?.ShopName || 'Store';
                commands.push(...Array.from(storeName).map(c => c.charCodeAt(0)));
                commands.push(0x0A); // Line feed
                commands.push(GS, 0x21, 0x00); // Reset size
                commands.push(ESC, 0x45, 0x00); // Bold off

                // Location
                const location = settings?.addr || '';
                if (location) {
                    commands.push(...Array.from(location).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }
                const location2 = settings?.addr2 || '';
                if (location2) {
                    commands.push(...Array.from(location2).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }


                let tpin =
                    settings?.Addr3 ||
                    store?.TPIN ||
                    store?.Tpin ||
                    store?.tpin ||
                    '';
                if (!tpin) {
                    const addr3 = settings?.Addr3 || store?.Addr3 || '';
                    const match = String(addr3).match(/\b\d{6,}\b/);
                    if (match) tpin = match[0];
                }
                if (tpin) {
                    commands.push(...Array.from(`TPIN : ${tpin}`).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }

                // Phone
                const phone = store?.Cell || '';
                if (phone) {

                    commands.push(...Array.from(phone).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }


                // OFFLINE RECEIPT indicator - Large, Bold, Center
                commands.push(ESC, 0x64, 0x01); // EMPTY LINE
                commands.push(ESC, 0x61, 0x01); // Center
                commands.push(ESC, 0x45, 0x01); // Bold on
                commands.push(...Array.from('Tax Invoice').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(GS, 0x21, 0x00); // Reset size
                commands.push(ESC, 0x45, 0x00); // Bold off
                commands.push(ESC, 0x64, 0x01); // EMPTY LINE

                // Left align for rest of receipt
                commands.push(ESC, 0x61, 0x00);

                // Date and time
                const now = new Date(receipt.timestamp);
                const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                commands.push(...Array.from(`Date ${dateStr}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Time ${timeStr}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Invoice number
                commands.push(...Array.from(`Invoice No.: ${receipt.invoice}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x64, 0x01);

                // Buyer Details (if any)
                try {
                    const custId = payload?.customer || context.getters.getCustomer;
                    if (custId) {
                        let debtor = payload?.customerDetails || null;
                        if (!debtor) {
                            // Attempt to find debtor details from store
                            const debtors = (store?.state?.accounts?.debtors) || [];
                            debtor = debtors.find(d => d.Id === custId) || null;
                        }
                        const buyerNameRaw = debtor?.Name || 'Customer';
                        const buyerSurname = debtor?.Surname || '';
                        const buyerName = `${buyerNameRaw} ${buyerSurname}`.trim() || 'Customer';
                        const buyerTPIN = debtor?.vat || debtor?.TPIN || '';
                        const buyerAddr = debtor?.Adress1 || debtor?.Address1 || debtor?.Area || '';
                        const buyerPhone = debtor?.CellNumber || debtor?.Phone || '';
                        const buyerBalance = typeof debtor?.Balance === 'number' ? debtor.Balance : null;

                        // Section header
                        commands.push(ESC, 0x61, 0x01); // Center
                        commands.push(ESC, 0x45, 0x01); // Bold
                        commands.push(...Array.from('Buyer Details').map(c => c.charCodeAt(0)));
                        commands.push(0x0A);
                        commands.push(ESC, 0x45, 0x00); // Bold off
                        commands.push(ESC, 0x61, 0x00); // Left

                        // Name
                        commands.push(...Array.from(`Name: ${buyerName}`).map(c => c.charCodeAt(0)));
                        commands.push(0x0A);

                        // TPIN if present
                        if (buyerTPIN) {
                            commands.push(...Array.from(`TPIN: ${buyerTPIN}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Address if present
                        if (buyerAddr) {
                            commands.push(...Array.from(`Address: ${buyerAddr}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Contact if present
                        if (buyerPhone) {
                            commands.push(...Array.from(`Contact: ${buyerPhone}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Balance if numeric
                        if (buyerBalance !== null) {
                            commands.push(...Array.from(`Balance: K${Number(buyerBalance).toFixed(2)}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Spacer
                        commands.push(ESC, 0x64, 0x01);
                    }
                } catch (e) {
                    // Non-blocking: if buyer details fail, continue silently
                }

                // Items header
                commands.push(ESC, 0x61, 0x01);
                commands.push(ESC, 0x45, 0x01); // Bold
                commands.push(...Array.from('Items').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x00);


                // Separator line
                commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                console.log("invoiceLines",invoiceLines)
                // Sale items - use items array from payload
                if (invoiceLines && Array.isArray(invoiceLines) && invoiceLines.length > 0) {
                    console.log('[Offline Print] item VATs:', invoiceLines.map(l => ({
                        Plu: l.Plu,
                        vat: l.vat,
                        Unit: l.Unit,
                        Qty: l.Qty
                    })));
                    invoiceLines.forEach(line => {
                        const plu = (line.Plu || '').substring(0, 6);
                        const desc = line.Description || 'Item';
                        const price = Number(line.Line || 0).toFixed(2);
                        const qty = line.Qty || 1;
                        const unit = Number(line.Unit || 0).toFixed(2);
                        const originalPrice = Number(line.originalPrice || line.baseUnit || line.Unit || 0);
                        const isSpecial = line.specialActive === true;

                        // Build item description - add SPECIAL indicator if active
                        let itemDesc = desc;
                        if (isSpecial) {
                            itemDesc = `${desc}`;
                        }

                        // PLU and description with price right-aligned
                        // Adjust itemDesc truncation based on paper width
                        const maxDescLength = lineWidth - 10 - price.length; // 10 = "K" + price digits + spacing
                        const truncatedDesc = itemDesc.length > maxDescLength
                            ? itemDesc.substring(0, maxDescLength)
                            : itemDesc;
                        const itemLine = `${plu}   ${truncatedDesc}`;
                        const spaces = Math.max(1, lineWidth - itemLine.length - price.length);
                        commands.push(...Array.from(itemLine).map(c => c.charCodeAt(0)));
                        commands.push(...Array.from(' '.repeat(spaces)).map(c => c.charCodeAt(0)));
                        commands.push(...Array.from(`K${price}`).map(c => c.charCodeAt(0)));


                        // Qty and unit price with VAT

                        commands.push(ESC, 0x61, 0x00);
                        // Print-side: per-line VAT is zeroed when onVat === false (outbox payload untouched).
                        const vat = (onVat ? Number(line.vat || 0) : 0).toFixed(3);
                        const qtyLine = `${qty} Qty ${unit} Each Vat ${vat}`;
                        commands.push(...Array.from(qtyLine).map(c => c.charCodeAt(0)));
                        commands.push(0x0A);

                        // If special, show the original price and savings
                        if (isSpecial && originalPrice > 0 && originalPrice !== unit) {
                            const savings = (originalPrice - unit) * qty;
                            // Show original price and savings - truncate if needed for narrow paper
                            let wasPriceLine = `ON SPECIAL WAS: K${originalPrice.toFixed(2)}  SAVED: K${savings.toFixed(2)}`;
                            if (wasPriceLine.length > lineWidth) {
                                wasPriceLine = `SPECIAL: K${originalPrice.toFixed(2)} S: K${savings.toFixed(2)}`;
                            }
                            if (wasPriceLine.length > lineWidth) {
                                wasPriceLine = wasPriceLine.substring(0, lineWidth);
                            }
                            commands.push(ESC, 0x45, 0x01); // Bold
                            commands.push(...Array.from(wasPriceLine).map(c => c.charCodeAt(0)));
                            commands.push(ESC, 0x45, 0x00); // Bold off
                            commands.push(0x0A);
                        } else if (isSpecial && originalPrice > 0) {
                            // Special but same price - just show original
                            let wasPriceLine = `ON SPECIAL WAS: K${originalPrice.toFixed(2)}`;
                            if (wasPriceLine.length > lineWidth) {
                                wasPriceLine = `SPECIAL: K${originalPrice.toFixed(2)}`;
                            }
                            if (wasPriceLine.length > lineWidth) {
                                wasPriceLine = wasPriceLine.substring(0, lineWidth);
                            }
                            commands.push(...Array.from(wasPriceLine).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        /**
                         * Render cooking/special instructions for the current line.
                         *
                         * Each line may carry an `instructions` array (shape: `{ CookingNum,
                         * CookingMsg, Price, count }`). The cost of those instructions is
                         * already rolled into `line.Line` (see `getLineNetTotal`), so this
                         * block only renders the human-readable text + per-instruction
                         * price breakdown — it MUST NOT mutate any totals.
                         *
                         * Added 2026-06-26: prior to this fix, the offline receipt printed
                         * the line total but not the instruction text, so the kitchen and
                         * customer had no way to see modifiers like "Extra Cheese" or
                         * "No Onions" once the order was offline.
                         */
                        if (Array.isArray(line.instructions) && line.instructions.length > 0) {
                            line.instructions.forEach(inst => {
                                // CookingMsg is the canonical field for instruction text in
                                // this codebase; fall back to other common field names to be
                                // resilient against any older payloads (matches the online
                                // receipt in OrdersService.js line 576).
                                const msg = String(
                                    inst.CookingMsg || inst.Message || inst.message || inst.Description || ''
                                ).trim();
                                if (!msg) return;

                                const unitPrice = Number(inst.Price || 0);
                                const count = Number(inst.count || inst.qty || 1);
                                const iCost = unitPrice * count;

                                // Build the line: "  + {msg}  K{cost}" when there's a cost,
                                // "  + {msg}" otherwise. Reserve 2 chars of left padding and
                                // the price suffix so we can hard-truncate to lineWidth.
                                const prefix = '  + ';
                                const suffix = iCost > 0 ? `  K${iCost.toFixed(2)}` : '';
                                const maxMsgLen = Math.max(1, lineWidth - prefix.length - suffix.length);
                                const truncatedMsg = msg.length > maxMsgLen ? msg.substring(0, maxMsgLen) : msg;
                                const instLine = `${prefix}${truncatedMsg}${suffix}`;

                                commands.push(ESC, 0x45, 0x01); // Bold on — visually distinguish instructions
                                commands.push(...Array.from(instLine).map(c => c.charCodeAt(0)));
                                commands.push(0x0A);
                                commands.push(ESC, 0x45, 0x00); // Bold off
                            });
                        }

                        // Separator line after each item
                        commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                        commands.push(0x0A);
                    });
                }

                // Separator
                // commands.push(...Array.from('-'.repeat(48)).map(c => c.charCodeAt(0)));
                // commands.push(0x0A);

                // Totals
                commands.push(ESC, 0x61, 0x02);

                commands.push(ESC, 0x45, 0x01); // Bold
                commands.push(...Array.from(`Excl Total K${printExclTotal.toFixed(3)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Tax Total K${printVat.toFixed(3)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x01); // Extra bold for sale total
                commands.push(...Array.from(` Sale Total: K${total.toFixed(2)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x00);

                commands.push(...Array.from(`Payment Type: ${paymentType}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Paid Amount: K${paidAmount.toFixed(2)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Change: K ${change.toFixed(2)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                commands.push(ESC, 0x61, 0x00); // LEFT ALIGN
                // Units bought (sum of item quantities)
                const unitsBought = (invoiceLines || []).reduce((sum, l) => sum + Number(l.Qty || 0), 0);
                commands.push(...Array.from(`Units Bought ${unitsBought}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Cashier
                // Cashier Name: the cashier who MADE the sale (payload.user,
                // stamped by parseInvoice at sale time) — never whoever is
                // logged in at print/reprint time.
                commands.push(...Array.from(`Cashier Name: ${payload?.user || 'N/A'}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Separator
                commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Sale reference should remain the numeric offline sale number
                const printedSaleNumber = receipt.saleNumber ?? payload.saleNumber ?? receipt.invoice;
                commands.push(...Array.from(`Sale No. ${printedSaleNumber}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A, 0x0A);
                if (reference) {
                    commands.push(...Array.from(`Reference: ${reference}`).map(c => c.charCodeAt(0)));
                    commands.push(0x0A, 0x0A);
                }

                // Separator
                commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                commands.push(ESC, 0x64, 0x02);// Feed 8 lines

                // Pending sync notice - Center, Bold
                commands.push(ESC, 0x61, 0x00); // Center
                commands.push(ESC, 0x45, 0x01); // Bold
                commands.push(...Array.from('*** PENDING SYNC ***').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from('Will sync when online').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x00);
                commands.push(ESC, 0x61, 0x00); // Left align

                // QR Code for offline receipt verification
                // console.log('store',store)
                // URL: https://overflow.abc.co.zm/OnlineSalesServer/invoice/{DB}/scanInvoiceQRCode/offline/{offlineInvoiceNumber}
                const qrUrl = `${window.serverApi}invoice/${store.Database}/scanInvoiceQRCode/offline/${receipt.invoice}`;

                // Center align for QR code
                commands.push(ESC, 0x61, 0x01); // Center
                commands.push(0x0A); // Line feed before QR

                // QR Code ESC/POS commands (GS (k) function for QR Code Model 2)
                // Set QR code model
                commands.push(GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00); // Model 2

                // Set QR code size (module size: 1-8, 3 is good for receipts)
                commands.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, 0x05); // Size 5

                // Set error correction level (48=L, 49=M, 50=Q, 51=H)
                commands.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x49); // Level M (49)

                // Store QR code data
                const qrDataLength = qrUrl.length + 3;
                const pL = qrDataLength & 0xFF;
                const pH = (qrDataLength >> 8) & 0xFF;
                commands.push(GS, 0x28, 0x6B, pL, pH, 0x31, 0x50, 0x30); // Store data
                commands.push(...Array.from(qrUrl).map(c => c.charCodeAt(0)));

                // Print QR code
                commands.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30); // Print stored QR code

                commands.push(0x0A); // Line feed after QR
                commands.push(ESC, 0x61, 0x00); // Left align

                // Thank you message
                commands.push(ESC, 0x61, 0x00); // Center
                commands.push(...Array.from('Thank you for using Lytsoft').map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Software by
                commands.push(...Array.from('Software by www.chrilantech.com').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x61, 0x00); // Left align

                // Feed and cut
                commands.push(0x0A, 0x0A, 0x0A, 0x0A); // Feed lines
                commands.push(ESC, 0x64, 0x08); // Feed 8 lines
                commands.push(GS, 0x56, 0x00); // Full cut

                // Convert to base64
                const buffer = new Uint8Array(commands);
                const base64 = btoa(String.fromCharCode.apply(null, buffer));

                // Try to print via Electron/hardware bridge
                if (window?.hardwareIPC || window?.electronBridge) {
                    const result = await hardwareBridge.printBase64({
                        deviceName: settings?.Printer || 'default',
                        base64,
                        addFeed: false, // Already included in commands
                        addCut: false   // Already included in commands
                    });
                    if (result?.ok) {
                        //console.log('[Offline Print] Receipt sent successfully');
                        return true;
                    } else {
                        console.error('[Offline Print] Failed to send print command:', result);
                        return false;
                    }
                }

                // Android mode
                if (window.androidMode) {
                    window.location.href = `rawbt:base64,${base64}`;
                    return true;
                }

                // Log if no printer available
                //console.log('[Offline Print] No printer available. Receipt data prepared:', base64.substring(0, 100) + '...');
                return true;

            } catch (err) {
                console.error('[Offline Print] Failed to print provisional receipt:', err);
                // Don't throw - printing failure shouldn't block the sale
                return false;
            }
        },

        BtPrint: async (context, payload) => {
            const [store, settings] = await Promise.all([
                Storage.getDefaultStore(),
                Storage.getDeviceSettings()
            ]);
            console.log('[Online Print] store:', store);
            console.log('[Online Print] settings:', settings);
            if (window?.hardwareIPC || window?.electronBridge) {
                console.log('[Online Print][Electron] about to use store info:', store);
            }
            const useDocumentReceipt = settings && (settings.A4Inv || settings.A4Type === 'A5');
            if (useDocumentReceipt) {
                let storeDB = store.StoreDB;
                return openUrl(appendDocumentPrintType(
                    franchiseApiLytning + `PrintApi/Quotes/${storeDB}/invoice/${payload.invoice}?PC=` + settings.PC,
                    settings
                ));
            }
            return axios.post(printVoid + `printing/${store.StoreDB}/printReceicept/${payload.invoice}/${payload.reprint}`, settings)
                .then(({ data }) => {
                    // Pass printer name with base64 to avoid duplicate storage call ⚡
                    return context.dispatch(`sendPrintToLocal`, {
                        base64: data,
                        printer: settings.Printer
                    });
                })
                .catch(err => errorHandler.tomcatError(err));
        },

        sendPrintToLocal: async (context, payload) => {
            // Handle both old (string) and new (object) formats for backward compatibility
            let base64, printer;

            if (typeof payload === 'string') {
                // Old format: just base64 string
                base64 = payload;
                // Fallback: load from storage (only if not passed)
                const settings = await Storage.getDeviceSettings();
                printer = settings?.Printer;
            } else {
                // New optimized format: object with base64 and printer
                base64 = payload.base64;
                printer = payload.printer;
            }

            // Fallback: if printer is empty/undefined, reload from storage (handles sync issues)
            if (!printer || !printer.trim()) {
                console.warn('[sendPrintToLocal] Printer empty, reloading from storage');
                const settings = await Storage.getDeviceSettings();
                printer = settings?.Printer;
                console.log('[sendPrintToLocal] Reloaded printer:', printer);
            }

            if (window.androidMode) {
                return window.location.href = `rawbt:base64,${base64}`
            }

            if (!printer || !printer.trim()) {
                return responses.showWarning('No printer selected')
            }

            // Electron first
            if (window?.hardwareIPC || window?.electronBridge) {
                const result = await hardwareBridge.printBase64({ printer, base64 });
                if (!result?.ok) {
                    // Show detailed error message
                    const errorMsg = result?.error || 'Failed to send print to Electron printer';
                    console.error('[sendPrintToLocal] Print failed:', result);
                    return responses.showWarning(errorMsg);
                }
                return true;
            }

            // Fallback to Local Hardware Service
            const obj = { printer, base64 };
            return axios.post(hardwareApi + 'printing/base64', obj);
        },

        printProforma: async (context, payload) => {
            let isAllowed = await validateSecurity(`ReprintBill`);
            if (!isAllowed) return;
            const [store, settings, user] = await Promise.all([
                Storage.getDefaultStore(),
                Storage.getDeviceSettings(),
                Storage.getCurrentUser()
            ]);
            if (settings && (settings.A4Inv || settings.A4Type === 'A5')) {
                return openUrl(appendDocumentPrintType(
                    franchiseApi + `SaveTable/PrintQuote/${payload}/${store.StoreDB}`,
                    settings
                ));
            }
            return axios.post(printVoid + `printing/${store.StoreDB}/printProforma/${payload}/${user.Username}`, settings)
                .then(({ data }) => {
                    try {
                        return context.dispatch(`sendPrintToLocal`, data);
                    } catch (e) {
                        responses.showWarning(`Please update your Hardware Service`)
                        if (window.androidMode) return window.location.href = `rawbt:base64,${data}`;
                        return axios.post(hardwareApi + `printing/printProforma/${store.StoreDB}/${payload}/${user.Username}`, settings)
                            .catch((err) => errorHandler.tomcatError(err, false, `Cannot Print Proforma Invoice`))
                    }
                })

        },
        printBillSlip: async (context, payload) => {
            let isAllowed = await validateSecurity(`ReprintBill`);
            if (!isAllowed) return;

            const [store, settings, user] = await Promise.all([
                Storage.getDefaultStore(),
                Storage.getDeviceSettings(),
                Storage.getCurrentUser()
            ]);
            if (settings && (settings.A4Inv || settings.A4Type === 'A5')) {
                return openUrl(appendDocumentPrintType(
                    franchiseApi + `SaveTable/PrintQuote/${payload}/${store.StoreDB}`,
                    settings
                ));
            }

            try {
                const response = await axios.post(printVoid + `printing/${store.StoreDB}/printBill/${payload}/${user.Username}`, settings);
                return context.dispatch('sendPrintToLocal', response.data)
            } catch (err) {
                errorHandler.tomcatError(err, false, `Bill Slip Print Error`)
            }



        },
        async getAllInvoices(context, payload) {
            let store = await Storage.getDefaultStore();
            payload = {
                ...context.getters.getGlobalDateRange,
                ...payload
            }
            return axios.post(backOfficeTomcatApi + `other/${store.StoreDB}/searchSales`, payload)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        async getInvoiceLines(context, inv) {
            let store = await Storage.getDefaultStore();
            return axios.get(serverApi + `invoice/${store.StoreDB}/getInvNum`, { params: { invoice: inv } })
                .then(({ data }) => data).catch((err) => errorHandler.tomcatError(err))
        },
        reprintLastInvoice: async (context) => {
            let isAllowed = await validateSecurity(`Reprint`);
            if (!isAllowed) return;
            $utils.showLoading()
            let latestInvoice = context.getters.getLatestInvoice;
            let invoice = latestInvoice.invoice;

            if (invoice) {
                // Check if this is an offline/provisional receipt
                if (latestInvoice.offline === true || latestInvoice.provisional === true) {
                    //console.log('[Reprint] Reprinting offline receipt:', invoice);
                    // Get the original payload from outbox if available
                    try {
                        const defaultStore = await Storage.getDefaultStore();
                        const outboxItem = await outbox.getOutboxItem(
                            invoice,
                            latestInvoice.storeDB || latestInvoice.storeDb || defaultStore?.StoreDB
                        );
                        if (outboxItem) {
                            return context.dispatch('printProvisionalReceipt', {
                                receipt: latestInvoice,
                                payload: outboxItem.payload
                            }).finally(() => $utils.hideLoading());
                        }
                    } catch (err) {
                        console.error('[Reprint] Could not fetch outbox item:', err);
                    }

                    // Fallback: create basic receipt from available data
                    return context.dispatch('printProvisionalReceipt', {
                        receipt: latestInvoice,
                        payload: {
                            invoicelines: [],
                            exclTotal: 0,
                            taxTotal: 0,
                            paymentType: 'Unknown'
                        }
                    }).finally(() => $utils.hideLoading());
                }

                // Online receipt - use normal printing
                return context.dispatch('BtPrint', { invoice: invoice, reprint: true })
                    .finally(() => $utils.hideLoading());
            }

            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `completedsale/${store.StoreDB}/getLastInvoiceNumber`)
                .then(({ data }) => context.dispatch('BtPrint', { invoice: data, reprint: true }))
                .catch((err) => errorHandler.tomcatError(err)).finally(() => $utils.hideLoading());
        },
        /**
         * Open a saved table into the active cart.
         * Hydrates stable line id from server fields so void + UPSERT keep the same PK (ticket 22844).
         * docs: FRONTEND.md cart line identity; BE MinifiedCurrentSale.id
         */
        openTable: async (context, tab) => {
            let store = await Storage.getDefaultStore();
            let saleNum = tab.saleNumber;

            try {
                const response = await axios.get(serverApi + `tables/${store.StoreDB}/openTable/${saleNum}`);
                if (response.status === 200) {
                    // Skip voided/archived server rows so they never re-enter the cart/invoice path.
                    // docs: src/utils/activeSaleLines.js
                    let items = filterActiveSaleLines(tab.items || []).map((item, index) => {
                        item.index = index;
                        if (!item.tourismLevy) {
                            item.tourismLevy = 0;
                        }
                        item.savedQty = item.Qty;
                        // Server may emit id, Id, or lineId — normalise to item.id for void/UPSERT.
                        if (!item.id) {
                            item.id = item.Id || item.lineId || item.currentSaleLineId || null;
                        }
                        return item;
                    });
                    await context.commit('setCurrentSale', tab.saleNumber);
                    await context.commit('setCurrentLines', items);
                    await context.dispatch('setCustomer', tab.customer);
                    if (isValidString(tab.customer)) {
                        await context.dispatch('getCustomerById', tab.customer);
                    }
                    await context.commit('setOpenTableName', tab.Name);
                    await context.dispatch('calculateTipAmount');
                    return true;
                }
            } catch (err) {
                errorHandler.tomcatError(err, 'Failed to open table', store);
            }
        },
        searchCashups: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `cashup/${store.StoreDB}/search`, payload)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        /**
         * Post item void to Online Sales Server (or queue offline via voidOutbox).
         * Returns `{ success, offline?, voidId?, businessReject?, status? }` so
         * voidItem/voidLine can gate cart removal (22844) and pick the right toast (#26827).
         * Business 4xx must not be treated as offline success — cart would desync from DB.
         * salenum==0: silent toasts — BE is best-effort audit; callers already remove locally (#26827).
         * docs: FRONTEND.md cart line identity; USER-FACING-ERRORS.md; services/voidOutbox.js
         */
        logItemVoid: async (context, payload) => {
            const [store, settings, user] = await Promise.all([
                Storage.getDefaultStore(),
                Storage.getDeviceSettings(),
                Storage.getCurrentUser()
            ]);
            // Prefer Username; fail-fast — never "System" (#26328)
            payload.User = requireSalesWriteUser(user, 'sales-module.logItemVoid');
            payload.PC = settings.PC;
            const saleNum = context.getters.getCurrentSale;
            payload.Reason = `Sale No. ${saleNum}`;
            // Explicit salenum for BE extractVoidSaleNumber (do not rely only on Reason parse)
            if (payload.salenum == null && payload.SaleNum == null) {
                payload.salenum = saleNum;
                payload.SaleNum = saleNum;
            }

            // Unsaved cart: local remove is the real outcome; never surface audit noise.
            // Why: Founder #26827 — cashiers saw businessReject toast after item already left cart.
            // Prefer payload salenum (callers stamp it); fall back to cart sale number.
            const effectiveSale = Number(
                payload.salenum != null ? payload.salenum
                    : (payload.SaleNum != null ? payload.SaleNum : saleNum)
            ) || 0;
            const isUnsavedAudit = effectiveSale === 0;
            const fireVoidToast = (opts) => {
                if (isUnsavedAudit) return;
                Toast.fire(opts);
            };

            // Print when Print Void is on for both walk-up cart (salenum=0) and
            // open/saved table. #26827 only silences toasts via fireVoidToast —
            // do not gate hardware print on isUnsavedAudit (#27081 Butcher Block).
            if (settings.PrintVoid) {
                await context.dispatch('printVoid', payload);
            }

            // Check connectivity status
            const isOnline = connectivityMonitor.getStatus();

            if (!isOnline) {
                // Offline: Queue void for later sync
                try {
                    const voidId = await voidOutbox.enqueueVoid(
                        payload,
                        store.StoreDB,
                        {
                            cashierName: user?.Username || 'Unknown',
                            cashierId: user?.UserId || null,
                            appVersion: '1.0.0'
                        }
                    );

                    if (voidId === null) {
                        console.warn('Void outbox store not available. Void cannot be queued for sync.');
                        fireVoidToast({
                            icon: 'warning',
                            title: 'Database Issue',
                            text: 'Unable to queue void for offline sync.'
                        });
                        return { success: false, offline: true };
                    }

                    // Show offline notification (saved sales only)
                    fireVoidToast({
                        icon: 'info',
                        title: 'Offline Mode',
                        text: 'Void queued for sync when online'
                    });

                    return { success: true, offline: true, voidId };
                } catch (err) {
                    console.error('Failed to enqueue offline void:', err);
                    fireVoidToast({
                        icon: 'error',
                        title: 'Void Error',
                        text: 'Failed to save void operation'
                    });
                    return { success: false, offline: true };
                }
            }

            // Online: Attempt to post immediately
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/logItemVoid`, payload, { timeout: 10000 })
                .then(() => {
                    return { success: true, offline: false };
                })
                .catch(async (err) => {
                    const status = err?.response?.status;
                    // Business rejection (400/406/409) — fail closed; do not fake offline success (22844).
                    // businessReject lets callers avoid the misleading "Check the connection" toast (#26827).
                    // Plain-language toast for cashiers; raw server body stays in logs only (USER-FACING-ERRORS.md).
                    if (status && status >= 400 && status < 500 && status !== 408) {
                        const raw = err?.response?.data;
                        const serverMsg = typeof raw === 'string'
                            ? raw
                            : (raw?.message || raw?.error || '');
                        console.warn('[logItemVoid] business reject', status, serverMsg);
                        // Founder exact copy was this toast on salenum=0 — now suppressed via fireVoidToast.
                        fireVoidToast({
                            icon: 'error',
                            title: 'Void failed',
                            text: 'The server could not process this void. Please refresh and try again.',
                        });
                        return { success: false, offline: false, status, businessReject: true };
                    }

                    // Network error or timeout: Fallback to offline mode
                    console.warn('Void logging failed, falling back to offline mode:', err);

                    try {
                        const voidId = await voidOutbox.enqueueVoid(
                            payload,
                            store.StoreDB,
                            {
                                cashierName: user?.Username || 'Unknown',
                                cashierId: user?.UserId || null,
                                appVersion: '1.0.0',
                                failedOnlineAttempt: true,
                                error: err.message
                            }
                        );

                        if (voidId === null) {
                            console.warn('Void outbox store not available.');
                            fireVoidToast({
                                icon: 'error',
                                title: 'Database Issue',
                                text: 'Unable to save void operation.'
                            });
                            return { success: false, offline: true };
                        }

                        fireVoidToast({
                            icon: 'warning',
                            title: 'Connection Issue',
                            text: 'Void queued and will sync automatically'
                        });

                        return { success: true, offline: true, voidId };
                    } catch (outboxErr) {
                        console.error('Failed to enqueue void after network error:', outboxErr);
                        fireVoidToast({
                            icon: 'error',
                            title: 'Void Error',
                            text: 'Failed to save void operation'
                        });
                        return { success: false, offline: true };
                    }
                });
        },
        printVoid: async (context, data) => {
            let submit;
            if (!data.items) {
                let items = [{
                    description: data.VoidDescritpiom,
                    plu: data.VoidPlu,
                    qty: data.VoidQty,
                    value: data.VoidValue
                }]

                submit = {
                    items,
                    pc: data.PC,
                    reason: data.Reason,
                    supervisor: data.SuperVisor,
                    user: data.User
                }

            } else {
                submit = data;
            }

            // Check connectivity and settings
            const isOnline = connectivityMonitor.getStatus();
            const settings = await Storage.getDeviceSettings();

            if (!isOnline) {
                // Offline: Generate ESC/POS commands locally for main printer
                await context.dispatch('printVoidOffline', submit);
                // Also print to item printers locally if enabled
                if (settings.PrintVoidToItemPrinter) {
                    try {
                        await context.dispatch('printVoidToItemPrinters', submit);
                    } catch (err) {
                        console.warn('[Void Print] Item printer void failed (non-critical):', err);
                    }
                }
            } else {
                // Online: always printVoidReceipt (not Routed); item printers stay local
                try {
                    let store = await Storage.getDefaultStore();
                    const response = await axios.post(printVoid + `printing/${store.StoreDB}/printVoidReceipt`, submit);
                    await context.dispatch('sendPrintToLocal', response.data);
                } catch (err) {
                    console.warn('[Void Print] Server printing failed, falling back to offline mode:', err);
                    await context.dispatch('printVoidOffline', submit);
                }
                if (settings.PrintVoidToItemPrinter) {
                    try {
                        await context.dispatch('printVoidToItemPrinters', submit);
                    } catch (err) {
                        console.warn('[Void Print] Item printer void failed (non-critical):', err);
                    }
                }
            }
        },

        printVoidOffline: async (context, data) => {
            // Generate void receipt locally using the shared ESC/POS builder
            try {
                let store = await Storage.getDefaultStore();
                let settings = await Storage.getDeviceSettings();

                const base64 = buildVoidReceiptEscPos(data, data.items, store, settings);

                // Print via Electron/hardware bridge
                if (window?.hardwareIPC || window?.electronBridge) {
                    const result = await hardwareBridge.printBase64({
                        deviceName: settings?.Printer || 'default',
                        base64,
                        addFeed: false,
                        addCut: false
                    });
                    if (result?.ok) {
                        return true;
                    } else {
                        console.error('[Void Print] Failed to send print command:', result?.error);
                        return false;
                    }
                }

                // Android mode
                if (window.androidMode) {
                    window.location.href = `rawbt:base64,${base64}`;
                    return true;
                }

                return false;

            } catch (err) {
                console.error('[Void Print] Failed to print offline void receipt:', err);
                return false;
            }
        },

        printVoidToItemPrinters: async (context, data) => {
            // Send void receipt to each voided item's assigned printer
            try {
                if (window.androidMode) return;

                const settings = await Storage.getDeviceSettings();
                if (!settings || !settings.PrintVoidToItemPrinter) return;

                const items = data.items || [];
                if (items.length === 0) return;

                // Group items by their assigned printer
                const printerGroups = {};

                for (const item of items) {
                    const plu = item.plu;
                    if (!plu) continue;

                    let stockSetting = null;
                    try {
                        stockSetting = await Storage.getStockSetting(plu);
                    } catch (_) {
                        continue;
                    }

                    const printerId = stockSetting?.printer || stockSetting?.Printer;
                    if (!printerId || String(printerId) === '0' || String(printerId) === '1000') {
                        continue;
                    }

                    const printerName = settings[`Printer${printerId}`];
                    if (!printerName) continue;

                    const key = String(printerId);
                    if (!printerGroups[key]) {
                        printerGroups[key] = { printerName, items: [] };
                    }
                    printerGroups[key].items.push(item);
                }

                const groupKeys = Object.keys(printerGroups);
                if (groupKeys.length === 0) return;

                const store = await Storage.getDefaultStore();

                for (const key of groupKeys) {
                    const group = printerGroups[key];
                    const base64 = buildVoidReceiptEscPos(data, group.items, store, settings);

                    if (window?.hardwareIPC || window?.electronBridge) {
                        const result = await hardwareBridge.printBase64({
                            printer: group.printerName,
                            base64,
                            addFeed: false,
                            addCut: false
                        });
                        if (!result?.ok) {
                            console.warn('[Void Print] Failed to print to item printer:', result?.error);
                        }
                    } else {
                        await axios.post(hardwareApi + 'printing/base64', {
                            printer: group.printerName,
                            base64
                        });
                    }
                }
            } catch (err) {
                console.error('[Void Print] Failed to print void to item printers:', err);
            }
        },

        openCashDrawer: async (context) => {
            try {
                // Skip for Android mode
                let isAndroid = window.androidMode;
                if (isAndroid) {
                    //console.log('[Cash Drawer] Skipping - Android mode');
                    return { ok: false, error: 'Not supported on Android' };
                }

                // Get settings for printer and cash drawer configuration
                let settings = await Storage.getDeviceSettings();

                // Use Electron IPC if available
                if (window?.hardwareIPC || window?.electronBridge) {
                    console.log('[Cash Drawer] Opening via Electron IPC...');

                    // CashDrawerKick setting contains the PRINTER NAME that controls the drawer
                    // The actual kick code is handled by main.js using standard ESC/POS commands
                    const payload = {
                        printerName: (settings?.CashDrawerKick || '').trim() || (settings?.Printer || '').trim()
                    };

                    console.log('[Cash Drawer] Using printer:', payload.printerName);

                    const result = await hardwareBridge.openCashDrawer(payload);

                    if (result) {
                        console.log('[Cash Drawer] Opened successfully via Electron');
                        return { ok: true };
                    } else {
                        console.error('[Cash Drawer] Failed to open via Electron');
                        return { ok: false, error: 'Failed to open cash drawer' };
                    }
                }

                // Fallback: this shouldn't be reached in Electron mode, but keeping for safety
                console.warn('[Cash Drawer] No Electron IPC available');
                return { ok: false, error: 'Hardware bridge not available' };

            } catch (err) {
                console.error('[Cash Drawer] Exception:', err);
                return { ok: false, error: String(err?.message || err) };
            }
        },

        getAllActiveSpecials: async (context) => {
            let store = await Storage.getDefaultStore()
            if (!store) return;
            let DB = store.StoreDB;
            return Promise.all([
                axios.get(serverApi + `specials/${DB}/getActiveSpecials?type=discount`)
                    .then(({ data }) => context.commit(`setDiscountSpecials`, data)),

                axios.get(serverApi + `specials/${DB}/getActiveSpecials?type=combo`)
                    .then(({ data }) => context.commit(`setComboSpecials`, data)),

                axios.get(serverApi + `specials/${DB}/getActiveSpecials?type=multi`)
                    .then(({ data }) => context.commit(`setMultiSpecials`, data)),
            ])
        },

        retrySmartInvoice: async (context, invoiceNumber) => {
            let store = await Storage.getDefaultStore()
            return axios.get(backOfficeTomcatApi + `other/${store.StoreDB}/sendSmartInvoice?invoice=` + invoiceNumber)
                .then(({ data }) => data)
                .catch(err => {
                    // Handle NullPointerException for missing refund number
                    if (err.response &&
                        err.response.status === 500 &&
                        err.response.data &&
                        err.response.data.response &&
                        err.response.data.response.type &&
                        err.response.data.response.type.includes("NullPointerException") &&
                        err.response.data.response.type.includes("getRefundNumber()")) {
                        return Swal.fire({
                            title: 'Smart Invoice Error',
                            icon: 'warning',
                            html: `<p>Unable to process smart invoice due to missing refund number.</p>
                                   <p>Please contact support: +260958168829</p>`,
                            showCloseButton: true,
                        });
                    }

                    const sanitizeErrorMessage = (message) => {
                        if (!message) return 'An error occurred';
                        return message.replace(/[<>]/g, match => match === '<' ? '&lt;' : '&gt;');
                    };

                    if (err.response && err.response.data && err.response.data.response) {
                        const rawErrorMessage = err.response.data.response.Message || err.response.data.response.message || 'An error occurred';
                        const errorMessage = sanitizeErrorMessage(rawErrorMessage);
                        const errorCode = err.response.data.response.Code || err.response.data.code || '';

                        return Swal.fire({
                            title: 'Smart Invoice Error',
                            icon: 'warning',
                            html: `${errorCode ? `<p>ZRA failure Code: ${errorCode}</p>` : ''}
                                   <p>${errorMessage}</p>
                                   <p>Please contact support: +260958168829</p>`,
                            showCloseButton: true,
                        });
                    }
                    return errorHandler.tomcatError(err);
                });
        },

    }
}
